import { Injectable, inject } from '@angular/core';
import { environment } from '../../../environments/environment';
import { BehaviorSubject, map } from 'rxjs';
import { AbstractControl, FormControl, FormGroup, Validators } from '@angular/forms';
import { MatTableDataSource } from '@angular/material/table';
import { DLE, DLECalculationInput, DLECalculationReport, DLECalculationResult, DLEDiffLiberation, DLEFullCompositionResult, DLEGasCompInput, DLEGasCompInputStr, DLEImageLog, DLEMaterialBalanceSummaryResult, DLEMasters, DLEPartialSave, DLEPropertyData, DLEReport, DLEReservoirToSTO, DLEResidualOilCompositions, DLESTOtoReservoirResult, DLEStageCalcInput, DLEStageCalcInputStr, DLEStageInput, DLEStageInputStr, DLEStdFluidBasis, DLEVaporStageCompositionResult, DLEVaporStageCompositions, DifferentialLiberationResult, DLMaterialBalanceCompleteData, Grid1, Grid2, Grid4, StatusParams, DleResponseState, DleFormSnapshots, DLEDiffLiberationDisplay, DLEGrid1Display, DLEGrid2Display, DLEImageLogCellValue, DLEImageLogColumn, DLEImageLogFormRow, DLESTOtoReservoirDisplay, DleChangeState, DleImageLogValidationIssue, DleImageLogValidationResult, DleStep1ValidationResult, DleStepValidationResult, DleTabDecision } from '../models/dle';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { APIResponse } from '../../models/APIResponse';
import { CVD_CONSTANTS, NUMERIC_CONSTANTS, PARAFFIN_CONSTANTS, PVT_CONSTANTS } from '../../common/constants';
import { DLE_CONSTANTS, DLEPropertyDataAdditionalTypes, commonConstants } from '../../common/experiment-constants';
import { MESSAGES_CONSTANTS } from '../../common/messages-constants';
import { NUMBERS_CONSTANTS } from '../../common/numbers-constants';
import { apiV1 } from '../../common/texts-constants';

@Injectable({
  providedIn: 'root'
})
export class DleService {
  private http = inject(HttpClient);
  router = inject(Router);

  private apiBaseUrl = environment.apiUrl+apiV1;

  public imageLogDataSub$ = new BehaviorSubject<DLEImageLog[]>([]);
  public emptyTube$ = new BehaviorSubject<number>(0);
  public blowdownTube$ = new BehaviorSubject<number>(0);
  public statusChange$ = new BehaviorSubject<StatusParams>({ statusId: 0, statusName: '' });
  private imageLogDataChangedSource = new BehaviorSubject<boolean>(false);
  isimageLogDataChanged$ = this.imageLogDataChangedSource.asObservable();
  private emptyTubeDataChangedSource = new BehaviorSubject<boolean>(false);
  isEmptyTubeDataChanged$ = this.emptyTubeDataChangedSource.asObservable();

  BuildDlePayload(
    imageLogValuesInput: DLEImageLog[],
    propertyInput: DLEPropertyData,
    emptyTube: number,
    stageInput: DLEStageInput[],
    gasCompInput: DLEGasCompInput[],
    stageCalcInput: DLEStageCalcInput[],
    stdFluidBasis: DLEStdFluidBasis[],
    includeStdFluidBasis: boolean,
    userId: string,
    role: string,
    labRepeatId: number,
    comments: string
  ): DLE {
    const normalizedProperty = this.NormalizePropertyDataWithDefaults({
      ...propertyInput,
      emptyTube,
    });

    return {
      dleImageLog: imageLogValuesInput,
      dlePropertyData: normalizedProperty,
      dleStageInput: stageInput,
      dleGasComp: gasCompInput,
      dleStageCalcInput: stageCalcInput,
      dleStdFluidBasis: includeStdFluidBasis ? stdFluidBasis : [],
      dleStdFluidBasisPVTsim: [],
      userId: userId,
      role: role,
      labRepeatId,
      comments,
    };
  }

  CanPersistDle(
    stageInput: DLEStageInput[],
    gasCompInput: DLEGasCompInput[],
    propertyInput: DLEPropertyData | undefined,
  ): boolean {
    return stageInput.length > 0 && gasCompInput.length > 0 && propertyInput !== undefined;
  }

  BuildDleStatus(statusId: number, statusName: string): StatusParams {
    return {
      statusId,
      statusName,
    };
  }

  BuildTabDecision(tabselection: boolean, selectedTab: number): DleTabDecision {
    return {
      tabselection: tabselection ? false : tabselection,
      selectedOldTab: selectedTab,
    };
  }

  ShouldNavigateAfterUpdate(statusName: string): boolean {
    const status = statusName.toLowerCase();
    return status === 'accepted' || status === 'rejected';
  }

  BuildResponseState(result: DLECalculationResult): DleResponseState {
    const analysis = this.BuildAnalysisState(result);
    const report = this.BuildReportState(result);

    return {
      imageLogValuesInput: result.dleImageLog,
      emptyTubeInput: result.dlePropertyData.emptyTube,
      dleCalculationInput: this.BuildCalculationInputState(result),
      analysisFinalCalculatedValue: analysis,
      dleReport: report,
      showResult: result.grid1Data !== null,
    };
  }

  BuildCalculationInputState(result: DLECalculationResult): DLECalculationInput {
    return {
      dlePropertyData: result.dlePropertyData,
      dleStageInput: result.dleStageInput,
      dleStageCalcInput: result.dleStageCalcInput,
      dleGasComp: result.dleGasComp,
      dleStdFluidBasis: result.dleStdFluidBasis,
      dleStdFluidBasisPVTsim: result.dleStdFluidBasisPVTsim,
      stageCalcData: result.stageCalcData,
    };
  }

  BuildAnalysisState(result: DLECalculationResult): DLMaterialBalanceCompleteData | undefined {
    if (result.grid1Data === null) {
      return undefined;
    }

    return {
      blowdownProperties: result.blowdownProperties,
      analysisProperties: result.analysisProperties,
      grid1Data: result.grid1Data,
      grid2Data: result.grid2Data,
      grid3Data: result.grid3Data,
      grid4Data: result.grid4Data,
      grid5Data: result.grid5Data,
      grid6Data: result.grid6Data,
    };
  }

  BuildReportState(result: DLECalculationResult): DLECalculationReport | undefined {
    if (result.diffLibResult === null) {
      return undefined;
    }

    return {
      diffLibResult: result.diffLibResult as DifferentialLiberationResult,
      vaporStageCompositionResult: result.vaporStageCompositionResult as DLEVaporStageCompositionResult,
      residualOilCompositionResult: result.residualOilCompositionResult as DLEResidualOilCompositions[],
      reservoirToSTOResult: result.reservoirToSTOResult as DLEReservoirToSTO[],
      stoToReservoirResult: result.stoToReservoirResult as DLESTOtoReservoirResult,
      fullCompositionResult: result.fullCompositionResult as DLEFullCompositionResult,
      materialBalanceSummaryResult: result.materialBalanceSummaryResult as DLEMaterialBalanceSummaryResult,
    };
  }

  NormalizePropertyDataWithDefaults(p: DLEPropertyData): DLEPropertyData {
    const copy = { ...p };
    copy.blowDownOilGravityAPI = this.ToNumberOrDefault(copy.blowDownOilGravityAPI);
    copy.blowDownOilDensity = this.ToNumberOrDefault(copy.blowDownOilDensity);
    return copy;
  }

  //Api call to Save dle details
  SaveDLE(dleDetails: DLE) {
    return this.http.post<APIResponse<DLECalculationResult>>(this.apiBaseUrl + 'dle/SaveDLE', dleDetails);
  }
  
  //Api call to Partial Save dle details
  PartiualSaveDLE(dleDetails: DLEPartialSave) {
    return this.http.post<APIResponse<DLEPartialSave>>(this.apiBaseUrl + 'dle/PartialSaveDLE', dleDetails);
  }

  //Api call to Get dle details
  GetDLE(testRepeatId: number) {
    return this.http.get<APIResponse<DLECalculationResult>>(this.apiBaseUrl + 'dle/GetDLE?testRepeatId=' + testRepeatId)
      .pipe(
        map(res => {
          if (res.isSuccess) {
            if (res.result !== null) {

              if (res.result.dleImageLog !== null && res.result.dleImageLog.length > 0) {
                this.imageLogDataSub$.next(res.result.dleImageLog);
              }
              if (res.result.dlePropertyData !== null) {
                this.emptyTube$.next(res.result.dlePropertyData.emptyTube);
              }
            }
          }
          return res;
        })

      );
  }
  //Api call to Get dle master details
  GetDLEMasters() {
    return this.http.get<APIResponse<DLEMasters>>(this.apiBaseUrl + 'dle/GetDLEMasters');
  }
  //Api call to Update dle details
  UpdateDLE(dleDetails: DLE) {
    return this.http.post<APIResponse<DLECalculationResult>>(this.apiBaseUrl + 'dle/UpdateDLE', dleDetails);
  }

  UpdateEmptyTube(emptyTube: number) {
    this.emptyTube$.next(emptyTube);
  }
  UpdateStatus(status: StatusParams) {
    this.statusChange$.next(status);
  }
  GetDLEReport(testRepeatId: number) {
          return this.http.get<APIResponse<DLEReport>>(this.apiBaseUrl + 'dle/GetDLEForFinalReport?testRepeatId=' + testRepeatId);
      }

  CheckAndReplaceNullValues<T extends object>(obj: T): T {
    const mutableObj = obj as Record<string, unknown>;
    for (const key of Object.keys(mutableObj)) {
      if (mutableObj[key] === undefined) {
        mutableObj[key] = null;
      }
    }
    return obj;
  }

  NormalizePropertyData(p: DLEPropertyData): DLEPropertyData {
     const copy = { ...p } as Omit<DLEPropertyData, DLEPropertyDataAdditionalTypes> & {
      blowDownOilGravityAPI: number | null;
      blowDownOilDensity: number | null;
    };
    copy.blowDownOilGravityAPI = this.ToNullableNumber(copy.blowDownOilGravityAPI);
    copy.blowDownOilDensity = this.ToNullableNumber(copy.blowDownOilDensity);
    return copy as DLEPropertyData;
  }

  ToNullableNumber(val: string | number | null | undefined): number | null {
    return val !== null && val !== '' && val !== undefined ? Number(val) : null;
  }

  GetGasCompIntValues(gasCompStr: DLEGasCompInputStr[]): DLEGasCompInput[] {
    return gasCompStr.map(
      (x: DLEGasCompInputStr) => {
        const convert = (val: string | number | null): number | null =>
        this.toNumber(this.ConvertDashOrBlankToNull(val));
        return {
          gasCompId: Number(x.gasCompId),
          gasComponents: x.gasComponents,
          blowDownCondensateWtPercent: convert(x.blowDownCondensateWtPercent === '--' || x.blowDownCondensateWtPercent === '' ? null : x.blowDownCondensateWtPercent),
          blowDownOilWtPercent: convert(x.blowDownOilWtPercent === '--' || x.blowDownOilWtPercent === '' ? null : x.blowDownOilWtPercent),
          resFluidFromSSF: convert(x.resFluidFromSSF === '--' || x.resFluidFromSSF === '' ? null : x.resFluidFromSSF),
          vaporStg1: convert(x.vaporStg1 === '--' || x.vaporStg1 === '' ? null : x.vaporStg1),
          vaporStg2: convert(x.vaporStg2 === '--' || x.vaporStg2 === '' ? null : x.vaporStg2),
          vaporStg3: convert(x.vaporStg3 === '--' || x.vaporStg3 === '' ? null : x.vaporStg3),
          vaporStg4: convert(x.vaporStg4 === '--' || x.vaporStg4 === '' ? null : x.vaporStg4),
          vaporStg5: convert(x.vaporStg5 === '--' || x.vaporStg5 === '' ? null : x.vaporStg5),
          vaporStg6: convert(x.vaporStg6 === '--' || x.vaporStg6 === '' ? null : x.vaporStg6),
          vaporStg7: convert(x.vaporStg7 === '--' || x.vaporStg7 === '' ? null : x.vaporStg7),
          vaporStg8: convert(x.vaporStg8 === '--' || x.vaporStg8 === '' ? null : x.vaporStg8),
          vaporStg9: convert(x.vaporStg9 === '--' || x.vaporStg9 === '' ? null : x.vaporStg9),
          vaporStg10: convert(x.vaporStg10 === '--' || x.vaporStg10 === '' ? null : x.vaporStg10),
          vaporStg11: convert(x.vaporStg11 === '--' || x.vaporStg11 === '' ? null : x.vaporStg11),
          vaporStg12: convert(x.vaporStg12 === '--' || x.vaporStg12 === '' ? null : x.vaporStg12),
          vaporStg13: convert(x.vaporStg13 === '--' || x.vaporStg13 === '' ? null : x.vaporStg13),
          vaporStg14: convert(x.vaporStg14 === '--' || x.vaporStg14 === '' ? null : x.vaporStg14),
          vaporStg15: convert(x.vaporStg15 === '--' || x.vaporStg15 === '' ? null : x.vaporStg15),
          vaporStg16: convert(x.vaporStg16 === '--' || x.vaporStg16 === '' ? null : x.vaporStg16),
          vaporStg17: convert(x.vaporStg17 === '--' || x.vaporStg17 === '' ? null : x.vaporStg17),
          vaporStg18: convert(x.vaporStg18 === '--' || x.vaporStg18 === '' ? null : x.vaporStg18),
          vaporStg19: convert(x.vaporStg19 === '--' || x.vaporStg19 === '' ? null : x.vaporStg19),
          vaporStg20: convert(x.vaporStg20 === '--' || x.vaporStg20 === '' ? null : x.vaporStg20),
          vaporBlowDownMolP: convert(x.vaporBlowDownMolP === '--' || x.vaporBlowDownMolP === '' ? null : x.vaporBlowDownMolP),
          blowdownTotalMolP: convert(x.blowdownTotalMolP === '--' || x.blowdownTotalMolP === '' ? null : x.blowdownTotalMolP),
          blowDownCondensateMol: convert(x.blowDownCondensateMol === '--' || x.blowDownCondensateMol === '' ? null : x.blowDownCondensateMol),
          blowDownCondensateMolP: convert(x.blowDownCondensateMolP === '--' || x.blowDownCondensateMolP === '' ? null : x.blowDownCondensateMolP),
        };
      }
    );
  }

  GetStageIntValues(stageStrValue: DLEStageInputStr[], convertIdToNumber = true): DLEStageInput[] {
    return stageStrValue.map((x: DLEStageInputStr) => {
      return {
        stageInputId: convertIdToNumber ? Number(x.stageInputId) : x.stageInputId,
        stage: x.stage,
        temperature: this.ConvertDashOrBlankToNull(x.temperature),
        pressure: this.ConvertDashOrBlankToNull(x.pressure),
        stageVVol: this.ConvertDashOrBlankToNull(x.stageVVol),
        ambientVVol: this.ConvertDashOrBlankToNull(x.ambientVVol),
        ambientTemp: this.ConvertDashOrBlankToNull(x.ambientTemp),
        ambientPressure: this.ConvertDashOrBlankToNull(x.ambientPressure),
        stageLVol: this.ConvertDashOrBlankToNull(x.stageLVol),
        tubeLiqVol: this.ConvertDashOrBlankToNull(x.tubeLiqVol),
        tubeLiqMass: this.ConvertDashOrBlankToNull(x.tubeLiqMass),
        vaporEmthermMolDensity: this.ConvertDashOrBlankToNull(x.vaporEmthermMolDensity),
        vaporEmthermGasDev: this.ConvertDashOrBlankToNull(x.vaporEmthermGasDev),
      } as DLEStageInput;
    });
  }

  GetStageCalcIntValues(stageCalcStr: DLEStageCalcInputStr[], convertIdToNumber = true): DLEStageCalcInput[] {
    return stageCalcStr.map((x: DLEStageCalcInputStr) => {
      return {
        stageCalcInputId: convertIdToNumber ? Number(x.stageCalcInputId) : x.stageCalcInputId,
        stage: x.stage,
        ambientVolume: this.toNumber(this.ConvertDashOrBlankToNull(x.ambientVolume)),
        ambientPressure: this.toNumber(this.ConvertDashOrBlankToNull(x.ambientPressure)),
        ambientTemperature: this.toNumber(this.ConvertDashOrBlankToNull(x.ambientTemperature)),
        volumeAtSTP: this.toNumber(this.ConvertDashOrBlankToNull(x.volumeAtSTP)),
      };
    });
  }

  GetStageInputsInDisplayOrder(inputData: DLEStageInput[]): (DLEStageInput | undefined)[] {
    const inputDataSorted: (DLEStageInput | undefined)[] = [];
    inputDataSorted.push(inputData.find((val) => val.stage === PVT_CONSTANTS.PSAT));

    const filteredData = inputData
      .filter((val) => val.stage !== PVT_CONSTANTS.PSAT && val.stage !== PVT_CONSTANTS.STO && val.stage !== PVT_CONSTANTS.BlowdownStage)
      .map((val) => ({
        ...val
      }))
      .sort((a, b) => Number(a.stage) - Number(b.stage));

    filteredData.forEach((item) => {
      inputDataSorted.push(item);
    });

    inputDataSorted.push(inputData.find((val) => val.stage === PVT_CONSTANTS.BlowdownStage));
    inputDataSorted.push(inputData.find((val) => val.stage === PVT_CONSTANTS.STO));
    return inputDataSorted;
  }

  GetVaporStageFlags(stageCount: number, maxStages = 20): boolean[] {
    const safeCount = Math.max(0, Math.min(stageCount, maxStages));
    return Array.from({ length: maxStages }, (_item, index) => index < safeCount);
  }

  BuildStageDefaultsFromMasters(stages: { stageName: string }[]): DLEStageInputStr[] {
    const invalidStages = new Set<string>([...DLE_CONSTANTS.invalidStages]);
    for (let i = NUMBERS_CONSTANTS.three; i <= NUMBERS_CONSTANTS.twenty; i++) {
      invalidStages.add(i.toString());
    }

    const defaults = stages
      .filter((stage) => !invalidStages.has(stage.stageName))
      .map((stage) => ({
        stageInputId: NUMBERS_CONSTANTS.zero,
        stage: stage.stageName,
        temperature: null,
        pressure: null,
        stageVVol: null,
        ambientVVol: null,
        ambientTemp: null,
        ambientPressure: null,
        stageLVol: null,
        tubeLiqVol: null,
        tubeLiqMass: null,
        vaporEmthermGasDev: null,
        vaporEmthermMolDensity: null,
      } as DLEStageInputStr));

    const stageIndex = defaults.findIndex((row) => row.stage === PVT_CONSTANTS.PSAT);
    if (stageIndex >= NUMBERS_CONSTANTS.zero) {
      defaults[stageIndex].stageVVol = commonConstants.doubleDash;
      defaults[stageIndex].ambientVVol = commonConstants.doubleDash;
      defaults[stageIndex].ambientTemp = commonConstants.doubleDash;
      defaults[stageIndex].ambientPressure = commonConstants.doubleDash;
    }

    const stageIndexSto = defaults.findIndex((row) => row.stage === PVT_CONSTANTS.STO);
    if (stageIndexSto >= NUMBERS_CONSTANTS.zero) {
      defaults[stageIndexSto].temperature = NUMBERS_CONSTANTS.sixty;
    }

    return defaults;
  }

  BuildGasCompDefaultsFromMasters(components: { component: string }[]): DLEGasCompInputStr[] {
    const createRow = (componentName: string): DLEGasCompInputStr => ({
      gasCompId: NUMBERS_CONSTANTS.zero,
      gasComponents: componentName,
      blowDownCondensateWtPercent: null,
      blowDownOilWtPercent: null,
      resFluidFromSSF: null,
      vaporStg1: null,
      vaporStg2: null,
      vaporStg3: null,
      vaporStg4: null,
      vaporStg5: null,
      vaporStg6: null,
      vaporStg7: null,
      vaporStg8: null,
      vaporStg9: null,
      vaporStg10: null,
      vaporStg11: null,
      vaporStg12: null,
      vaporStg13: null,
      vaporStg14: null,
      vaporStg15: null,
      vaporStg16: null,
      vaporStg17: null,
      vaporStg18: null,
      vaporStg19: null,
      vaporStg20: null,
      vaporBlowDownMolP: null,
      blowdownTotalMolP: null,
      blowDownCondensateMol: null,
      blowDownCondensateMolP: null,
    });

    const defaults = components.map((component) => createRow(component.component));
    defaults.push(createRow(DLE_CONSTANTS.total));
    return defaults;
  }

  BuildStdFluidBasisDefaults(
    components: { component: string }[],
    stageCount: number
  ): DLEStdFluidBasis[] {
    return components.map((item) => {
      const row: Record<string, number | string> = {
        stdFluidBasisModelId: NUMBERS_CONSTANTS.zero,
        component: item.component,
      };

      for (let i = NUMBERS_CONSTANTS.one; i <= stageCount; i++) {
        row[`vaporStg${i}`] = NUMBERS_CONSTANTS.zero;
      }

      row[DLE_CONSTANTS.vaporLastStage] = NUMBERS_CONSTANTS.zero;
      return row as unknown as DLEStdFluidBasis;
    });
  }

  BuildExcelUploadColumns(baseColumns: string[], vaporDisplayNames: string[], vaporFlags: boolean[]): string[] {
    const columns = [...baseColumns];
    vaporFlags.forEach((flag, index) => {
      if (flag && vaporDisplayNames[index]) {
        columns.push(vaporDisplayNames[index]);
      }
    });
    return columns;
  }

  CreateStageCalcDataSource(stageCalcControls: AbstractControl[]): MatTableDataSource<AbstractControl> {
    return new MatTableDataSource(stageCalcControls);
  }

  BuildStdFluidBasisColumns(stageCount: number): string[] {
    const columns: string[] = [CVD_CONSTANTS.component, CVD_CONSTANTS.vaporstg1, CVD_CONSTANTS.vaporstg2];
    for (let i = NUMBERS_CONSTANTS.three; i <= stageCount; i++) {
      columns.push(`vaporStg${i}`);
    }
    columns.push(DLE_CONSTANTS.vaporLastStage);
    return columns;
  }

  RoundTo(num: number, places: number): number {
    const factor = NUMBERS_CONSTANTS.ten ** places;
    return Math.round(num * factor) / factor;
  }

  FilterGasCompColumnsByVaporFlags(columns: string[], vaporFlags: boolean[]): string[] {
    return columns.filter((col) => {
      const match = /^vaporstg(\d+)$/i.exec(col);
      if (!match) {
        return true;
      }
      const stageNumber = Number(match[1]);
      if (Number.isNaN(stageNumber) || stageNumber <= 0 || stageNumber > vaporFlags.length) {
        return true;
      }
      return vaporFlags[stageNumber - 1];
    });
  }

  ClearHiddenVaporValues(rows: DLEGasCompInputStr[], vaporFlags: boolean[], maxStagesToClear = 10): DLEGasCompInputStr[] {
    const maxStages = Math.min(maxStagesToClear, vaporFlags.length);
    for (let i = 1; i <= maxStages; i++) {
      if (!vaporFlags[i - 1]) {
        const vaporKey = `vaporStg${i}` as keyof DLEGasCompInputStr;
        (rows as DLEGasCompInputStr[]).forEach((row) => {
          (row[vaporKey] as string | number | null) = null;
        });
      }
    }
    return rows;
  }

  ParseTabularPaste(pastedText: string): string[][] {
    const normalized = pastedText.replace(/\r\n/g, '\n').replace(/\r/g, '\n');
    const rows = normalized.split('\n').filter((row) => row.trim() !== commonConstants.emptyString);
    return rows.map((row) => row.split('\t'));
  }

  BuildGasCompPasteRows(parsedRows: string[][]): DLEGasCompInputStr[] {
    return parsedRows.map((columns) => ({
      gasCompId: NUMBERS_CONSTANTS.zero,
      gasComponents: commonConstants.emptyString,
      blowDownCondensateWtPercent: columns[0] ? Number(columns[0]) : NUMBERS_CONSTANTS.zero,
      blowDownOilWtPercent: columns[1] ? Number(columns[1]) : NUMBERS_CONSTANTS.zero,
      resFluidFromSSF: columns[2] ? Number(columns[2]) : NUMBERS_CONSTANTS.zero,
      vaporStg1: columns[3] ? Number(columns[3]) : NUMBERS_CONSTANTS.zero,
      vaporStg2: columns[4] ? Number(columns[4]) : NUMBERS_CONSTANTS.zero,
      vaporStg3: columns[5] ? Number(columns[5]) : NUMBERS_CONSTANTS.zero,
      vaporStg4: null,
      vaporStg5: null,
      vaporStg6: null,
      vaporStg7: null,
      vaporStg8: null,
      vaporStg9: null,
      vaporStg10: null,
      vaporStg11: null,
      vaporStg12: null,
      vaporStg13: null,
      vaporStg14: null,
      vaporStg15: null,
      vaporStg16: null,
      vaporStg17: null,
      vaporStg18: null,
      vaporStg19: null,
      vaporStg20: null,
      vaporBlowDownMolP: columns[6] ? Number(columns[6]) : NUMBERS_CONSTANTS.zero,
      blowdownTotalMolP: columns[7] ? Number(columns[7]) : NUMBERS_CONSTANTS.zero,
      blowDownCondensateMol: NUMBERS_CONSTANTS.zero,
      blowDownCondensateMolP: NUMBERS_CONSTANTS.zero,
    }));
  }

  MergeGasCompPasteRows(existingRows: DLEGasCompInputStr[], parsedRows: DLEGasCompInputStr[]): DLEGasCompInputStr[] {
    const merged = existingRows.map((row) => ({ ...row }));

    for (let i = 0; i < merged.length; i++) {
      if (i < parsedRows.length) {
        merged[i].blowDownCondensateWtPercent = parsedRows[i].blowDownCondensateWtPercent;
        merged[i].blowDownOilWtPercent = parsedRows[i].blowDownOilWtPercent;
        merged[i].resFluidFromSSF = parsedRows[i].resFluidFromSSF;
        merged[i].vaporStg1 = parsedRows[i].vaporStg1;
        merged[i].vaporStg2 = parsedRows[i].vaporStg2;
        merged[i].vaporStg3 = parsedRows[i].vaporStg3;
        merged[i].vaporStg4 = parsedRows[i].vaporStg4;
        merged[i].vaporStg5 = parsedRows[i].vaporStg5;
        merged[i].vaporStg6 = parsedRows[i].vaporStg6;
        merged[i].vaporStg7 = parsedRows[i].vaporStg7;
        merged[i].vaporStg8 = parsedRows[i].vaporStg8;
        merged[i].vaporStg9 = parsedRows[i].vaporStg9;
        merged[i].vaporStg10 = parsedRows[i].vaporStg10;
        merged[i].vaporStg11 = parsedRows[i].vaporStg11;
        merged[i].vaporStg12 = parsedRows[i].vaporStg12;
        merged[i].vaporStg13 = parsedRows[i].vaporStg13;
        merged[i].vaporStg14 = parsedRows[i].vaporStg14;
        merged[i].vaporStg15 = parsedRows[i].vaporStg15;
        merged[i].vaporStg16 = parsedRows[i].vaporStg16;
        merged[i].vaporStg17 = parsedRows[i].vaporStg17;
        merged[i].vaporStg18 = parsedRows[i].vaporStg18;
        merged[i].vaporStg19 = parsedRows[i].vaporStg19;
        merged[i].vaporStg20 = parsedRows[i].vaporStg20;
        merged[i].vaporBlowDownMolP = parsedRows[i].vaporBlowDownMolP;
        merged[i].blowdownTotalMolP = parsedRows[i].blowdownTotalMolP;
        merged[i].blowDownCondensateMol = parsedRows[i].blowDownCondensateMol;
        merged[i].blowDownCondensateMolP = parsedRows[i].blowDownCondensateMolP;
      } else {
        merged[i].blowDownCondensateWtPercent = commonConstants.emptyString;
        merged[i].blowDownOilWtPercent = commonConstants.emptyString;
        merged[i].resFluidFromSSF = commonConstants.emptyString;
        merged[i].vaporStg1 = commonConstants.emptyString;
        merged[i].vaporStg2 = commonConstants.emptyString;
        merged[i].vaporStg3 = commonConstants.emptyString;
        merged[i].vaporStg4 = commonConstants.emptyString;
        merged[i].vaporStg5 = commonConstants.emptyString;
        merged[i].vaporStg6 = commonConstants.emptyString;
        merged[i].vaporStg7 = commonConstants.emptyString;
        merged[i].vaporStg8 = commonConstants.emptyString;
        merged[i].vaporStg9 = commonConstants.emptyString;
        merged[i].vaporStg10 = commonConstants.emptyString;
        merged[i].vaporStg11 = commonConstants.emptyString;
        merged[i].vaporStg12 = commonConstants.emptyString;
        merged[i].vaporStg13 = commonConstants.emptyString;
        merged[i].vaporStg14 = commonConstants.emptyString;
        merged[i].vaporStg15 = commonConstants.emptyString;
        merged[i].vaporStg16 = commonConstants.emptyString;
        merged[i].vaporStg17 = commonConstants.emptyString;
        merged[i].vaporStg18 = commonConstants.emptyString;
        merged[i].vaporStg19 = commonConstants.emptyString;
        merged[i].vaporStg20 = commonConstants.emptyString;
        merged[i].vaporBlowDownMolP = commonConstants.emptyString;
        merged[i].blowdownTotalMolP = commonConstants.emptyString;
        merged[i].blowDownCondensateMol = commonConstants.emptyString;
        merged[i].blowDownCondensateMolP = commonConstants.emptyString;
      }
    }

    return merged;
  }

  BuildStagePasteRows(parsedRows: string[][]): DLEStageInputStr[] {
    return parsedRows.map((columns) => ({
      stageInputId: NUMBERS_CONSTANTS.zero,
      stage: commonConstants.emptyString,
      temperature: columns[1] ? Number(columns[1]) : null,
      pressure: columns[2] ? Number(columns[2]) : null,
      stageVVol: !(columns[0] === PVT_CONSTANTS.PSAT || columns[0] === PVT_CONSTANTS.STO) && columns[3] ? Number(columns[3]) : null,
      ambientVVol: !(columns[0] === PVT_CONSTANTS.PSAT) && columns[4] ? Number(columns[4]) : null,
      ambientTemp: !(columns[0] === PVT_CONSTANTS.PSAT) && columns[5] ? Number(columns[5]) : null,
      ambientPressure: !(columns[0] === PVT_CONSTANTS.PSAT) && columns[6] ? Number(columns[6]) : null,
      stageLVol: columns[7] ? Number(columns[7]) : null,
      tubeLiqVol: !(columns[0] === PVT_CONSTANTS.PSAT) && columns[8] ? Number(columns[8]) : null,
      tubeLiqMass: !(columns[0] === PVT_CONSTANTS.PSAT) && columns[9] ? Number(columns[9]) : null,
      vaporEmthermMolDensity: null,
      vaporEmthermGasDev: null,
    }));
  }

  MergeStagePasteRows(existingRows: DLEStageInputStr[], parsedRows: DLEStageInputStr[]): DLEStageInputStr[] {
    const merged = existingRows.map((row) => ({ ...row }));

    for (let i = 0; i < merged.length; i++) {
      if (i < parsedRows.length) {
        merged[i].temperature = parsedRows[i].temperature;
        merged[i].pressure = parsedRows[i].pressure;
        merged[i].stageVVol = parsedRows[i].stageVVol;
        merged[i].ambientVVol = parsedRows[i].ambientVVol;
        merged[i].ambientTemp = parsedRows[i].ambientTemp;
        merged[i].ambientPressure = parsedRows[i].ambientPressure;
        merged[i].stageLVol = parsedRows[i].stageLVol;
        merged[i].tubeLiqVol = parsedRows[i].tubeLiqVol;
        merged[i].tubeLiqMass = parsedRows[i].tubeLiqMass;
      } else {
        merged[i].temperature = null;
        merged[i].pressure = null;
        merged[i].stageVVol = null;
        merged[i].ambientVVol = null;
        merged[i].ambientTemp = null;
        merged[i].ambientPressure = null;
        merged[i].stageLVol = null;
        merged[i].tubeLiqVol = null;
        merged[i].tubeLiqMass = null;
      }
    }

    return merged;
  }

  ApplyStageTemperature(stageRows: DLEStageInputStr[], value: string | number | null): DLEStageInputStr[] {
    return stageRows.map((row) => {
      if (row.stage !== PVT_CONSTANTS.STO) {
        return { ...row, temperature: value };
      }
      return row;
    });
  }

  ApplyStageDefaultValues(stageRows: DLEStageInputStr[]): DLEStageInputStr[] {
    return stageRows.map((row) => {
      if (row.stage === PVT_CONSTANTS.PSAT) {
        return {
          ...row,
          stageVVol: commonConstants.doubleDash,
          ambientVVol: commonConstants.doubleDash,
          ambientTemp: commonConstants.doubleDash,
          ambientPressure: commonConstants.doubleDash,
          tubeLiqVol: commonConstants.doubleDash,
          tubeLiqMass: commonConstants.doubleDash,
          vaporEmthermMolDensity: commonConstants.doubleDash,
          vaporEmthermGasDev: commonConstants.doubleDash,
        };
      }
      if (row.stage === PVT_CONSTANTS.STO) {
        return {
          ...row,
          temperature: NUMBERS_CONSTANTS.sixty,
          stageVVol: commonConstants.doubleDash,
          ambientVVol: commonConstants.doubleDash,
          ambientTemp: commonConstants.doubleDash,
          ambientPressure: commonConstants.doubleDash,
          tubeLiqVol: commonConstants.doubleDash,
          tubeLiqMass: commonConstants.doubleDash,
          vaporEmthermMolDensity: commonConstants.emptyString,
          vaporEmthermGasDev: commonConstants.emptyString,
        };
      }
      if (row.stage === PVT_CONSTANTS.BlowdownStage) {
        return {
          ...row,
          stageVVol: commonConstants.doubleDash,
        };
      }
      return row;
    });
  }

  ValidateStep1Form(initialForm: FormGroup, runRangeChecks: boolean): DleStep1ValidationResult {
    const invalidFieldIds: string[] = [];
    const warningMessages: string[] = [];

    DLE_CONSTANTS.initialFormControls.forEach((controlName) => {
      const control = initialForm.get(controlName);
      control?.setValidators([Validators.required]);
      control?.updateValueAndValidity({ onlySelf: true, emitEvent: false });
      const value = control?.value;
      if (value === null || value === commonConstants.emptyString || value === commonConstants.doubleDash) {
        invalidFieldIds.push(controlName);
      }
    });
    initialForm.markAllAsTouched();

    if (runRangeChecks) {
      const rangeChecks: { field: string; min: number; max: number; label: string }[] = [
        { field: CVD_CONSTANTS.reservoirtemperature, min: NUMBERS_CONSTANTS.zero, max: NUMBERS_CONSTANTS.threeFifty, label: CVD_CONSTANTS.reservoirTemperature },
        { field: CVD_CONSTANTS.volincell, min: NUMBERS_CONSTANTS.zero, max: NUMBERS_CONSTANTS.twoFifty, label: CVD_CONSTANTS.volInCell },
        { field: DLE_CONSTANTS.densityOfFluidAtVol, min: NUMBERS_CONSTANTS.zero, max: NUMBERS_CONSTANTS.two, label: DLE_CONSTANTS.densityOfFluidAtVol2 },
        { field: DLE_CONSTANTS.oilDensityAtPsat, min: NUMBERS_CONSTANTS.zero, max: NUMBERS_CONSTANTS.two, label: DLE_CONSTANTS.oilDensityAtPsat2 },
        { field: DLE_CONSTANTS.volOfOilAtSTO, min: NUMBERS_CONSTANTS.zero, max: NUMBERS_CONSTANTS.twoHundred, label: DLE_CONSTANTS.volOfOilAtSTO2 },
        { field: DLE_CONSTANTS.blowDownOilDensity, min: NUMBERS_CONSTANTS.zero, max: NUMBERS_CONSTANTS.two, label: DLE_CONSTANTS.blowDownOilDensity2 },
        { field: DLE_CONSTANTS.blowDownOilMW, min: NUMBERS_CONSTANTS.fifty, max: NUMBERS_CONSTANTS.fiveHundred, label: DLE_CONSTANTS.blowDownOilMW2 },
        { field: DLE_CONSTANTS.blowDownOilC50Plus, min: NUMBERS_CONSTANTS.fourHundred, max: NUMBERS_CONSTANTS.twoThousand, label: DLE_CONSTANTS.blowDownOilC50Plus2 },
        { field: DLE_CONSTANTS.ssfMWofResFluid, min: NUMBERS_CONSTANTS.zero, max: NUMBERS_CONSTANTS.threeHundred, label: DLE_CONSTANTS.ssfMWofResFluid2 },
        { field: CVD_CONSTANTS.standardp, min: NUMBERS_CONSTANTS.ten, max: NUMBERS_CONSTANTS.twenty, label: CVD_CONSTANTS.standardP },
        { field: CVD_CONSTANTS.standardt, min: NUMBERS_CONSTANTS.sixty, max: NUMBERS_CONSTANTS.eighty, label: CVD_CONSTANTS.standardT },
      ];

      rangeChecks.forEach((range) => {
        const value = initialForm.controls[range.field]?.value;
        if (this.HasInputValue(value) && (Number(value) < range.min || Number(value) > range.max)) {
          warningMessages.push(`${CVD_CONSTANTS.inputValueOutOfRange}${range.label}`);
        }
      });
    }

    const error = initialForm.invalid ? MESSAGES_CONSTANTS.enterDataInAllTheRequiredFields : commonConstants.emptyString;
    return {
      status: error === commonConstants.emptyString,
      error,
      hasWarning: warningMessages.length > NUMBERS_CONSTANTS.zero,
      warningMessages,
      invalidFieldIds,
    };
  }

  ExtractFormRows<T>(data: unknown[]): T[] {
    return data.map((row) => {
      const value = row as { getRawValue?: () => T; controls?: Record<string, { value: unknown }> } & T;
      if (typeof value.getRawValue === 'function') {
        return value.getRawValue();
      }
      if (value.controls) {
        const normalized: Record<string, unknown> = {};
        Object.keys(value.controls).forEach((key) => {
          normalized[key] = value.controls?.[key]?.value;
        });
        return normalized as T;
      }
      return value;
    });
  }

  CreateStageEmptyRow(stage: number, reservoirTemperature: string | number | null): DLEStageInputStr {
    return {
      stageInputId: NUMBERS_CONSTANTS.zero,
      stage: stage.toString(),
      temperature: reservoirTemperature,
      pressure: commonConstants.emptyString,
      stageVVol: commonConstants.emptyString,
      ambientVVol: commonConstants.emptyString,
      ambientTemp: commonConstants.emptyString,
      ambientPressure: commonConstants.emptyString,
      stageLVol: commonConstants.emptyString,
      tubeLiqVol: commonConstants.emptyString,
      tubeLiqMass: commonConstants.emptyString,
      vaporEmthermMolDensity: null,
      vaporEmthermGasDev: null,
    };
  }

  GetPreviousStageNumberBeforeBlowdown(stageRows: DLEStageInputStr[]): number {
    const blowdownIndex = stageRows.findIndex((row) => row.stage === PVT_CONSTANTS.BlowdownStage);
    if (blowdownIndex <= NUMBERS_CONSTANTS.one) {
      return NUMBERS_CONSTANTS.zero;
    }
    return Number(stageRows[blowdownIndex - NUMBERS_CONSTANTS.one]?.stage ?? NUMBERS_CONSTANTS.zero);
  }

  GetStageCountBeforeBlowdown(stageRows: DLEStageInputStr[]): number {
    const blowdownIndex = stageRows.findIndex((row) => row.stage === PVT_CONSTANTS.BlowdownStage);
    return blowdownIndex - NUMBERS_CONSTANTS.one;
  }

  RenumberStageRowsAfterDelete(stageRows: DLEStageInputStr[], deletedIndex: number): DLEStageInputStr[] {
    const rows = stageRows.map((row) => ({ ...row }));
    const blowdownIndex = rows.findIndex((row) => row.stage === PVT_CONSTANTS.BlowdownStage);
    if (deletedIndex === blowdownIndex) {
      return rows;
    }

    for (let index = deletedIndex; index < blowdownIndex; index++) {
      const previousStage = rows[index - NUMBERS_CONSTANTS.one]?.stage;
      const previousIndexValue = previousStage === PVT_CONSTANTS.PSAT ? NUMBERS_CONSTANTS.zero : Number(previousStage ?? NUMBERS_CONSTANTS.zero);
      rows[index].stage = (previousIndexValue + NUMBERS_CONSTANTS.one).toString();
    }

    return rows;
  }

  GetDleCellPasteColumns(table: string, displayedColumns: string[], gasCompFormColumns: string[], vaporFlags: boolean[]): string[] {
    switch (table.toLowerCase()) {
      case CVD_CONSTANTS.stageinput2:
        return displayedColumns.includes(CVD_CONSTANTS.delete)
          ? displayedColumns.filter((column) => column !== CVD_CONSTANTS.delete)
          : displayedColumns;
      case DLE_CONSTANTS.stagecalcinput:
        return [...DLE_CONSTANTS.columns];
      case CVD_CONSTANTS.gascompform:
        return this.FilterGasCompColumnsByVaporFlags(gasCompFormColumns, vaporFlags);
      default:
        return [];
    }
  }

  ApplyDleCellPaste<T extends Record<string, unknown>>(rows: T[], pastedData: string, rowIndex: number, column: string, columns: string[]): T[] {
    const normalized = pastedData.replace(/\r\n/g, '\n').replace(/\r/g, '\n');
    const splitRows = normalized.split('\n');
    if (splitRows.length && splitRows[splitRows.length - NUMBERS_CONSTANTS.one] === commonConstants.emptyString) {
      splitRows.pop();
    }

    const updatedRows = rows.map((row) => ({ ...row }));
    const parsedData = splitRows.map((row) => row.split('\t'));
    parsedData.forEach((cols, parsedRowIndex) => {
      const targetRow = rowIndex + parsedRowIndex;
      const startColIndex = columns.indexOf(column);
      cols.forEach((value, colOffset) => {
        const colName = columns[startColIndex + colOffset];
        if (colName && updatedRows[targetRow]) {
          updatedRows[targetRow][colName as keyof T] = (value?.trim() || null) as T[keyof T];
        }
      });
    });

    return updatedRows;
  }

  ValidateStep3Rows(data: DLEGasCompInputStr[], runRangeChecks: boolean): DleStepValidationResult {
    const validator = /^-?\d+(\.\d+)?$/;
    const errorMessages: string[] = [];
    const warningMessages: string[] = [];
    const invalidFieldIds: string[] = [];
    let sumResidualFluidSsf = NUMBERS_CONSTANTS.zero;

    const addError = (message: string, fieldId: string): void => {
      errorMessages.push(message);
      invalidFieldIds.push(fieldId);
    };
    const addWarning = (label: string, rowIndex: number): void => {
      warningMessages.push(`${CVD_CONSTANTS.inputValueOutOfRange}${label}${CVD_CONSTANTS.forRowNumber}${rowIndex + NUMBERS_CONSTANTS.one}`);
    };
    const checkNumeric = (
      value: string | number | null | undefined,
      messagePrefix: string,
      fieldPrefix: string,
      rowIndex: number
    ): void => {
      if (this.HasInputValue(value) && !validator.test(String(value))) {
        addError(`${messagePrefix}${rowIndex + NUMBERS_CONSTANTS.one}`, `${fieldPrefix}${rowIndex}`);
      }
    };
    const checkRange = (
      min: number,
      max: number,
      label: string,
      rowIndex: number,
      value: string | number | null | undefined
    ): void => {
      if (this.HasInputValue(value)) {
        const numeric = Number(value);
        if (numeric < min || numeric > max) {
          addWarning(label, rowIndex);
        }
      }
    };

    for (let i = NUMBERS_CONSTANTS.zero; i < data.length; i++) {
      const row = data[i];

      checkNumeric(row.blowDownCondensateWtPercent, MESSAGES_CONSTANTS.enterValidValueForBlowdownCondensateForRowNumber, MESSAGES_CONSTANTS.blowDownCondensateWtPercent, i);
      checkNumeric(row.blowDownOilWtPercent, MESSAGES_CONSTANTS.enterValidValueForBlowdownOilForRowNumber, MESSAGES_CONSTANTS.blowDownOilWtPercent, i);
      checkNumeric(row.resFluidFromSSF, MESSAGES_CONSTANTS.enterValidValueForResFluidFromSsfForRowNumber, MESSAGES_CONSTANTS.msgResfluidfromssf2, i);
      checkNumeric(row.vaporStg1, MESSAGES_CONSTANTS.pleaseEnterValidValueForVaporStageForRowNumber(1), MESSAGES_CONSTANTS.msgVaporstg12, i);
      checkNumeric(row.vaporStg2, MESSAGES_CONSTANTS.pleaseEnterValidValueForVaporStageForRowNumber(2), MESSAGES_CONSTANTS.msgVaporstg22, i);
      checkNumeric(row.vaporStg3, MESSAGES_CONSTANTS.pleaseEnterValidValueForVaporStageForRowNumber(3), MESSAGES_CONSTANTS.msgVaporstg32, i);
      checkNumeric(row.vaporStg4, MESSAGES_CONSTANTS.pleaseEnterValidValueForVaporStageForRowNumber(4), MESSAGES_CONSTANTS.msgVaporstg42, i);
      checkNumeric(row.vaporStg5, MESSAGES_CONSTANTS.pleaseEnterValidValueForVaporStageForRowNumber(5), MESSAGES_CONSTANTS.msgVaporstg52, i);
      checkNumeric(row.vaporStg6, MESSAGES_CONSTANTS.pleaseEnterValidValueForVaporStageForRowNumber(6), MESSAGES_CONSTANTS.msgVaporstg62, i);
      checkNumeric(row.vaporStg7, MESSAGES_CONSTANTS.pleaseEnterValidValueForVaporStageForRowNumber(7), MESSAGES_CONSTANTS.msgVaporstg72, i);
      checkNumeric(row.vaporStg8, MESSAGES_CONSTANTS.pleaseEnterValidValueForVaporStageForRowNumber(8), MESSAGES_CONSTANTS.msgVaporstg82, i);
      checkNumeric(row.vaporStg9, MESSAGES_CONSTANTS.pleaseEnterValidValueForVaporStageForRowNumber(9), MESSAGES_CONSTANTS.msgVaporstg92, i);
      checkNumeric(row.vaporStg10, MESSAGES_CONSTANTS.pleaseEnterValidValueForVaporStageForRowNumber(10), MESSAGES_CONSTANTS.msgVaporstg102, i);
      checkNumeric(row.vaporStg11, MESSAGES_CONSTANTS.pleaseEnterValidValueForVaporStageForRowNumber(11), MESSAGES_CONSTANTS.msgVaporstg112, i);
      checkNumeric(row.vaporStg12, MESSAGES_CONSTANTS.pleaseEnterValidValueForVaporStageForRowNumber(12), MESSAGES_CONSTANTS.msgVaporstg122, i);
      checkNumeric(row.vaporStg13, MESSAGES_CONSTANTS.pleaseEnterValidValueForVaporStageForRowNumber(13), MESSAGES_CONSTANTS.msgVaporstg132, i);
      checkNumeric(row.vaporStg14, MESSAGES_CONSTANTS.pleaseEnterValidValueForVaporStageForRowNumber(14), MESSAGES_CONSTANTS.msgVaporstg142, i);
      checkNumeric(row.vaporStg15, MESSAGES_CONSTANTS.pleaseEnterValidValueForVaporStageForRowNumber(15), MESSAGES_CONSTANTS.msgVaporstg152, i);
      checkNumeric(row.vaporStg16, MESSAGES_CONSTANTS.pleaseEnterValidValueForVaporStageForRowNumber(16), MESSAGES_CONSTANTS.msgVaporstg162, i);
      checkNumeric(row.vaporStg17, MESSAGES_CONSTANTS.pleaseEnterValidValueForVaporStageForRowNumber(17), MESSAGES_CONSTANTS.msgVaporstg172, i);
      checkNumeric(row.vaporStg18, MESSAGES_CONSTANTS.pleaseEnterValidValueForVaporStageForRowNumber(18), MESSAGES_CONSTANTS.msgVaporstg182, i);
      checkNumeric(row.vaporStg19, MESSAGES_CONSTANTS.pleaseEnterValidValueForVaporStageForRowNumber(19), MESSAGES_CONSTANTS.msgVaporstg192, i);
      checkNumeric(row.vaporStg20, MESSAGES_CONSTANTS.pleaseEnterValidValueForVaporStageForRowNumber(20), MESSAGES_CONSTANTS.msgVaporstg202, i);
      checkNumeric(row.vaporBlowDownMolP, MESSAGES_CONSTANTS.pleaseEnterValidValueForVaporBlowDownForRowNumber, MESSAGES_CONSTANTS.vaporBlowDownMolP2, i);

      sumResidualFluidSsf += this.ToNumberOrDefault(row.resFluidFromSSF, NUMBERS_CONSTANTS.zero);

      if (runRangeChecks) {
        checkRange(NUMBERS_CONSTANTS.zero, NUMBERS_CONSTANTS.hundred, DLE_CONSTANTS.blowDownCondensate, i, row.blowDownCondensateWtPercent);
        checkRange(NUMBERS_CONSTANTS.zero, NUMBERS_CONSTANTS.hundred, DLE_CONSTANTS.blowDownOil, i, row.blowDownOilWtPercent);
        checkRange(NUMBERS_CONSTANTS.zero, NUMBERS_CONSTANTS.hundred, CVD_CONSTANTS.resFluidFromSsf, i, row.resFluidFromSSF);
        checkRange(NUMBERS_CONSTANTS.zero, NUMBERS_CONSTANTS.hundred, DLE_CONSTANTS.vaporStage1, i, row.vaporStg1);
        checkRange(NUMBERS_CONSTANTS.zero, NUMBERS_CONSTANTS.hundred, DLE_CONSTANTS.vaporStage2, i, row.vaporStg2);
        checkRange(NUMBERS_CONSTANTS.zero, NUMBERS_CONSTANTS.hundred, DLE_CONSTANTS.vaporStage3, i, row.vaporStg3);
        checkRange(NUMBERS_CONSTANTS.zero, NUMBERS_CONSTANTS.hundred, DLE_CONSTANTS.vaporStage4, i, row.vaporStg4);
        checkRange(NUMBERS_CONSTANTS.zero, NUMBERS_CONSTANTS.hundred, DLE_CONSTANTS.vaporStage5, i, row.vaporStg5);
        checkRange(NUMBERS_CONSTANTS.zero, NUMBERS_CONSTANTS.hundred, DLE_CONSTANTS.vaporStage6, i, row.vaporStg6);
        checkRange(NUMBERS_CONSTANTS.zero, NUMBERS_CONSTANTS.hundred, DLE_CONSTANTS.vaporStage7, i, row.vaporStg7);
        checkRange(NUMBERS_CONSTANTS.zero, NUMBERS_CONSTANTS.hundred, DLE_CONSTANTS.vaporStage8, i, row.vaporStg8);
        checkRange(NUMBERS_CONSTANTS.zero, NUMBERS_CONSTANTS.hundred, DLE_CONSTANTS.vaporStage9, i, row.vaporStg9);
        checkRange(NUMBERS_CONSTANTS.zero, NUMBERS_CONSTANTS.hundred, DLE_CONSTANTS.vaporStage10, i, row.vaporStg10);
        checkRange(NUMBERS_CONSTANTS.zero, NUMBERS_CONSTANTS.hundred, DLE_CONSTANTS.vaporStage11, i, row.vaporStg11);
        checkRange(NUMBERS_CONSTANTS.zero, NUMBERS_CONSTANTS.hundred, DLE_CONSTANTS.vaporStage12, i, row.vaporStg12);
        checkRange(NUMBERS_CONSTANTS.zero, NUMBERS_CONSTANTS.hundred, DLE_CONSTANTS.vaporStage13, i, row.vaporStg13);
        checkRange(NUMBERS_CONSTANTS.zero, NUMBERS_CONSTANTS.hundred, DLE_CONSTANTS.vaporStage14, i, row.vaporStg14);
        checkRange(NUMBERS_CONSTANTS.zero, NUMBERS_CONSTANTS.hundred, DLE_CONSTANTS.vaporStage15, i, row.vaporStg15);
        checkRange(NUMBERS_CONSTANTS.zero, NUMBERS_CONSTANTS.hundred, DLE_CONSTANTS.vaporStage16, i, row.vaporStg16);
        checkRange(NUMBERS_CONSTANTS.zero, NUMBERS_CONSTANTS.hundred, DLE_CONSTANTS.vaporStage17, i, row.vaporStg17);
        checkRange(NUMBERS_CONSTANTS.zero, NUMBERS_CONSTANTS.hundred, DLE_CONSTANTS.vaporStage18, i, row.vaporStg18);
        checkRange(NUMBERS_CONSTANTS.zero, NUMBERS_CONSTANTS.hundred, DLE_CONSTANTS.vaporStage19, i, row.vaporStg19);
        checkRange(NUMBERS_CONSTANTS.zero, NUMBERS_CONSTANTS.hundred, DLE_CONSTANTS.vaporStage20, i, row.vaporStg20);
        checkRange(NUMBERS_CONSTANTS.zero, NUMBERS_CONSTANTS.hundred, DLE_CONSTANTS.vaporBlowDown, i, row.vaporBlowDownMolP);
      }
    }

    if (sumResidualFluidSsf <= NUMBERS_CONSTANTS.zero) {
      errorMessages.push(MESSAGES_CONSTANTS.pleaseEnterDataForResFluidSsf);
    }

    return {
      isValid: errorMessages.length === NUMBERS_CONSTANTS.zero,
      hasWarning: warningMessages.length > NUMBERS_CONSTANTS.zero,
      errorMessages,
      warningMessages,
      invalidFieldIds,
    };
  }

  ValidateStep2Rows(data: DLEStageInputStr[], stageCalc: DLEStageCalcInputStr[], runRangeChecks: boolean): DleStepValidationResult {
    const validator = /^-?\d+(\.\d+)?$/;
    const validatorAllowDashNum = /^[-]+$|^[0-9.]*$/;
    const errorMessages: string[] = [];
    const warningMessages: string[] = [];
    const invalidFieldIds: string[] = [];

    const addError = (message: string, fieldId: string): void => {
      errorMessages.push(message);
      invalidFieldIds.push(fieldId);
    };
    const addWarning = (label: string, rowIndex: number): void => {
      warningMessages.push(`${CVD_CONSTANTS.inputValueOutOfRange}${label}${CVD_CONSTANTS.forRowNumber}${rowIndex + NUMBERS_CONSTANTS.one}`);
    };
    const isNumeric = (value: string | number | null | undefined): boolean => validator.test(String(value));
    const isNumericOrDash = (value: string | number | null | undefined): boolean => validatorAllowDashNum.test(String(value));
    const checkRange = (
      min: number,
      max: number,
      label: string,
      rowIndex: number,
      value: string | number | null | undefined
    ): void => {
      if (this.HasInputValue(value)) {
        const numeric = Number(value);
        if (numeric < min || numeric > max) {
          addWarning(label, rowIndex);
        }
      }
    };

    for (let i = NUMBERS_CONSTANTS.zero; i < data.length; i++) {
      const row = data[i];

      if (
        this.HasInputValue(row.tubeLiqVol) &&
        !isNumeric(row.tubeLiqVol) &&
        row.stage !== PVT_CONSTANTS.PSAT &&
        row.stage !== PVT_CONSTANTS.STO
      ) {
        addError(`${MESSAGES_CONSTANTS.pleaseEnterValidValueForTubeLiqVolForRowNumber}${i + NUMBERS_CONSTANTS.one}`, `${MESSAGES_CONSTANTS.tubeLiqVol}${i}`);
      }

      if (
        this.HasInputValue(row.tubeLiqMass) &&
        !isNumeric(row.tubeLiqMass) &&
        row.stage !== PVT_CONSTANTS.PSAT &&
        row.stage !== PVT_CONSTANTS.STO
      ) {
        addError(`${MESSAGES_CONSTANTS.enterValidValueForTubeLiqMassForRowNumber}${i + NUMBERS_CONSTANTS.one}`, `${DLE_CONSTANTS.tubeLiqMass2}${i}`);
      }

      if (row.stage === DLE_CONSTANTS.numberOne) {
        if (!this.HasInputValue(row.temperature) || !isNumeric(row.temperature)) {
          addError(`${MESSAGES_CONSTANTS.enterValidValueForTemperatureForRowNumber}${i + NUMBERS_CONSTANTS.one}`, `${CVD_CONSTANTS.temperature3}${i}`);
        }
        if (!this.HasInputValue(row.pressure) || !isNumeric(row.pressure)) {
          addError(`${MESSAGES_CONSTANTS.enterValidValueForPressureForRowNumber}${i + NUMBERS_CONSTANTS.one}`, `${CVD_CONSTANTS.pressure3}${i}`);
        }
        if (!this.HasInputValue(row.stageVVol) || !isNumeric(row.stageVVol)) {
          addError(`${MESSAGES_CONSTANTS.pleaseEnterValidValueForStagevvolForRowNumber}${i + NUMBERS_CONSTANTS.one}`, `${DLE_CONSTANTS.stageVVol3}${i}`);
        }
        if (!this.HasInputValue(row.ambientVVol) || !isNumeric(row.ambientVVol)) {
          addError(`${MESSAGES_CONSTANTS.enterValidValueForAmbientvvolForRowNumber}${i + NUMBERS_CONSTANTS.one}`, `${DLE_CONSTANTS.ambientVoVol}${i}`);
        }
        if (!this.HasInputValue(row.ambientTemp) || !isNumeric(row.ambientTemp)) {
          addError(`${MESSAGES_CONSTANTS.enterValidValueForAmbienttempForRowNumber}${i + NUMBERS_CONSTANTS.one}`, `${CVD_CONSTANTS.ambienttemp2}${i}`);
        }
        if (!this.HasInputValue(row.ambientPressure) || !isNumericOrDash(row.ambientPressure)) {
          addError(`${MESSAGES_CONSTANTS.enterValidValueForAmbientpressureForRowNumber}${i + NUMBERS_CONSTANTS.one}`, `${CVD_CONSTANTS.ambientpressure2}${i}`);
        }
        if (!this.HasInputValue(row.stageLVol) || !isNumeric(row.stageLVol)) {
          addError(`${MESSAGES_CONSTANTS.enterValidValueForStagelvolForRowNumber}${i + NUMBERS_CONSTANTS.one}`, `${CVD_CONSTANTS.stagelvol2}${i}`);
        }
      }

      if (row.stage === PVT_CONSTANTS.PSAT || row.stage === PVT_CONSTANTS.STO || row.stage === PVT_CONSTANTS.BlowdownStage) {
        if (!this.HasInputValue(row.pressure) || !isNumeric(row.pressure)) {
          addError(`${MESSAGES_CONSTANTS.enterValidValueForPressureForRowNumber}${i + NUMBERS_CONSTANTS.one}`, `${CVD_CONSTANTS.pressure3}${i}`);
        }
        if (!this.HasInputValue(row.stageLVol) || !isNumeric(row.stageLVol)) {
          addError(`${MESSAGES_CONSTANTS.enterValidValueForStagelvolForRowNumber}${i + NUMBERS_CONSTANTS.one}`, `${CVD_CONSTANTS.stagelvol2}${i}`);
        }
        if (row.stage === PVT_CONSTANTS.PSAT && (!this.HasInputValue(row.temperature) || !isNumeric(row.temperature))) {
          addError(`${MESSAGES_CONSTANTS.enterValidValueForTemperatureForRowNumber}${i + NUMBERS_CONSTANTS.one}`, `${CVD_CONSTANTS.temperature3}${i}`);
        }
        if (this.HasInputValue(row.stageVVol) && !isNumericOrDash(row.stageVVol) && row.stage !== PVT_CONSTANTS.PSAT) {
          addError(`${MESSAGES_CONSTANTS.pleaseEnterValidValueForStagevvolForRowNumber}${i + NUMBERS_CONSTANTS.one}`, `${DLE_CONSTANTS.stageVVol3}${i}`);
        }
        if (this.HasInputValue(row.ambientVVol) && !isNumericOrDash(row.ambientVVol) && row.stage !== PVT_CONSTANTS.PSAT) {
          addError(`${MESSAGES_CONSTANTS.enterValidValueForAmbientvvolForRowNumber}${i + NUMBERS_CONSTANTS.one}`, `${MESSAGES_CONSTANTS.msgAmbientvovol2}${i}`);
        }
        if (this.HasInputValue(row.ambientTemp) && !isNumericOrDash(row.ambientTemp) && row.stage !== PVT_CONSTANTS.PSAT) {
          addError(`${MESSAGES_CONSTANTS.enterValidValueForAmbienttempForRowNumber}${i + NUMBERS_CONSTANTS.one}`, `${MESSAGES_CONSTANTS.msgAmbienttemp2}${i}`);
        }
        if (this.HasInputValue(row.ambientPressure) && !isNumericOrDash(row.ambientPressure) && row.stage !== PVT_CONSTANTS.PSAT) {
          addError(`${MESSAGES_CONSTANTS.enterValidValueForAmbientpressureForRowNumber}${i + NUMBERS_CONSTANTS.one}`, `${CVD_CONSTANTS.ambientpressure2}${i}`);
        }
      } else if (row.stage === DLE_CONSTANTS.numberOne || row.stage.toLowerCase() === MESSAGES_CONSTANTS.msgExtrastep3) {
        if (!this.HasInputValue(row.vaporEmthermMolDensity) || !isNumeric(row.vaporEmthermMolDensity)) {
          addError(`${MESSAGES_CONSTANTS.pleaseEnterValidValueForVaporEmthermMolDensityForRowNumber}${i + NUMBERS_CONSTANTS.one}`, `${CVD_CONSTANTS.vaporemthermmoldensity2}${i}`);
        }
        if (!this.HasInputValue(row.vaporEmthermGasDev) || !isNumeric(row.vaporEmthermGasDev)) {
          addError(`${MESSAGES_CONSTANTS.pleaseEnterValidValueForVaporEmthermGasDevForRowNumber}${i + NUMBERS_CONSTANTS.one}`, `${CVD_CONSTANTS.vaporemthermgasdev2}${i}`);
        }
      } else {
        if (this.HasInputValue(row.temperature) && !isNumeric(row.temperature)) {
          addError(`${MESSAGES_CONSTANTS.enterValidValueForTemperatureForRowNumber}${i + NUMBERS_CONSTANTS.one}`, `${CVD_CONSTANTS.temperature3}${i}`);
        }
        if (this.HasInputValue(row.pressure) && !isNumeric(row.pressure)) {
          addError(`${MESSAGES_CONSTANTS.enterValidValueForPressureForRowNumber}${i + NUMBERS_CONSTANTS.one}`, `${CVD_CONSTANTS.pressure3}${i}`);
        }
        if (this.HasInputValue(row.stageVVol) && !isNumeric(row.stageVVol)) {
          addError(`${MESSAGES_CONSTANTS.pleaseEnterValidValueForStagevvolForRowNumber}${i + NUMBERS_CONSTANTS.one}`, `${DLE_CONSTANTS.stageVVol3}${i}`);
        }
        if (this.HasInputValue(row.ambientVVol) && !isNumeric(row.ambientVVol)) {
          addError(`${MESSAGES_CONSTANTS.enterValidValueForAmbientvvolForRowNumber}${i + NUMBERS_CONSTANTS.one}`, `${MESSAGES_CONSTANTS.msgAmbientvovol2}${i}`);
        }
        if (this.HasInputValue(row.ambientTemp) && !isNumeric(row.ambientTemp)) {
          addError(`${MESSAGES_CONSTANTS.enterValidValueForAmbienttempForRowNumber}${i + NUMBERS_CONSTANTS.one}`, `${MESSAGES_CONSTANTS.msgAmbienttemp2}${i}`);
        }
        if (this.HasInputValue(row.ambientPressure) && !isNumericOrDash(row.ambientPressure)) {
          addError(`${MESSAGES_CONSTANTS.enterValidValueForAmbientpressureForRowNumber}${i + NUMBERS_CONSTANTS.one}`, `${MESSAGES_CONSTANTS.msgAmbientpressure2}${i}`);
        }
        if (this.HasInputValue(row.stageLVol) && !isNumeric(row.stageLVol)) {
          addError(`${MESSAGES_CONSTANTS.enterValidValueForStagelvolForRowNumber}${i + NUMBERS_CONSTANTS.one}`, `${CVD_CONSTANTS.stagelvol2}${i}`);
        }
        if (this.HasInputValue(row.vaporEmthermMolDensity) && !isNumeric(row.vaporEmthermMolDensity)) {
          addError(`${MESSAGES_CONSTANTS.pleaseEnterValidValueForVaporEmthermMolDensityForRowNumber}${i + NUMBERS_CONSTANTS.one}`, `${MESSAGES_CONSTANTS.msgVaporemthermmoldensity2}${i}`);
        }
        if (this.HasInputValue(row.vaporEmthermGasDev) && !isNumeric(row.vaporEmthermGasDev)) {
          addError(`${MESSAGES_CONSTANTS.pleaseEnterValidValueForVaporEmthermGasDevForRowNumber}${i + NUMBERS_CONSTANTS.one}`, `${CVD_CONSTANTS.vaporemthermgasdev2}${i}`);
        }
      }

      if (runRangeChecks) {
        checkRange(NUMERIC_CONSTANTS.fifty, NUMERIC_CONSTANTS.threeHundredFifty, CVD_CONSTANTS.temperature, i, row.temperature);
        checkRange(NUMERIC_CONSTANTS.zero, NUMERIC_CONSTANTS.fifteenThousand, CVD_CONSTANTS.pressure2, i, row.pressure);
        checkRange(NUMERIC_CONSTANTS.zero, NUMERIC_CONSTANTS.fiveHundred, DLE_CONSTANTS.stageVVol2, i, row.stageVVol);
        checkRange(NUMERIC_CONSTANTS.zero, NUMERIC_CONSTANTS.fiftyFiveThousand, CVD_CONSTANTS.ambientVVol, i, row.ambientVVol);
        checkRange(NUMERIC_CONSTANTS.sixty, NUMERIC_CONSTANTS.eighty, CVD_CONSTANTS.ambientTemp, i, row.ambientTemp);
        checkRange(NUMERIC_CONSTANTS.ten, NUMERIC_CONSTANTS.twenty, CVD_CONSTANTS.ambientPressure, i, row.ambientPressure);
        checkRange(NUMERIC_CONSTANTS.zero, NUMERIC_CONSTANTS.twoHundredFifty, CVD_CONSTANTS.stageLVol, i, row.stageLVol);
        checkRange(NUMERIC_CONSTANTS.zero, NUMERIC_CONSTANTS.ten, CVD_CONSTANTS.tubeLiqVol, i, row.tubeLiqVol);
        checkRange(NUMERIC_CONSTANTS.zero, NUMERIC_CONSTANTS.ten, CVD_CONSTANTS.tubeLiqMass, i, row.tubeLiqMass);
        checkRange(NUMERIC_CONSTANTS.fourEminus5, NUMERIC_CONSTANTS.fourPointFiveEminus5, CVD_CONSTANTS.vaporEmthermMolDensity, i, row.vaporEmthermMolDensity);
        checkRange(NUMERIC_CONSTANTS.pointFive, NUMERIC_CONSTANTS.two, CVD_CONSTANTS.vaporEmthermGasDev, i, row.vaporEmthermGasDev);
      }
    }

    for (let i = NUMBERS_CONSTANTS.zero; i < stageCalc.length; i++) {
      const row = stageCalc[i];
      if (!isNumericOrDash(row.ambientPressure) && row.ambientPressure !== null) {
        addError(`${MESSAGES_CONSTANTS.pleaseEnterValidValueForAmbientPressureForRowNumber}${i + NUMBERS_CONSTANTS.one}`, `${MESSAGES_CONSTANTS.msgAmbientpressure1}${i}`);
      }
      if (!isNumeric(row.ambientTemperature) && row.ambientTemperature !== null) {
        addError(`${MESSAGES_CONSTANTS.pleaseEnterValidValueForAmbientTemperatureForRowNumber}${i + NUMBERS_CONSTANTS.one}`, `${MESSAGES_CONSTANTS.msgAmbienttemperature2}${i}`);
      }
      if (!isNumeric(row.ambientVolume) && row.ambientVolume !== null) {
        addError(`${MESSAGES_CONSTANTS.pleaseEnterValidValueForAmbientVolumeForRowNumber}${i + NUMBERS_CONSTANTS.one}`, `${MESSAGES_CONSTANTS.msgAmbientvolume2}${i}`);
      }
    }

    return {
      isValid: errorMessages.length === NUMBERS_CONSTANTS.zero,
      hasWarning: warningMessages.length > NUMBERS_CONSTANTS.zero,
      errorMessages,
      warningMessages,
      invalidFieldIds,
    };
  }

  NormalizeStateValue(obj: unknown): Record<string, unknown> | unknown {
    if (Array.isArray(obj)) {
      return obj.map((item) => this.NormalizeStateValue(item));
    }

    if (obj !== null && typeof obj === 'object') {
      const newObj: Record<string, unknown> = {};

      Object.keys(obj).forEach((key) => {
        let value = (obj as Record<string, unknown>)[key];

        if (
          value === PARAFFIN_CONSTANTS.emptyString ||
          value === undefined ||
          value === null ||
          (typeof value === 'string' && value.trim() === PARAFFIN_CONSTANTS.emptyString)
        ) {
          value = null;
        }

        if (
          typeof value === 'string' &&
          value.trim() !== PARAFFIN_CONSTANTS.emptyString &&
          !isNaN(Number(value))
        ) {
          value = Number(value);
        }

        newObj[key] = this.NormalizeStateValue(value);
      });

      return newObj;
    }

    return obj;
  }

  BuildFormSnapshots(values: {
    initialForm: unknown;
    stageForm: unknown;
    stageCalcForm: unknown;
    gasCompForm: unknown;
  }): DleFormSnapshots {
    return {
      initialFormValue: this.NormalizeStateValue(values.initialForm) as Record<string, unknown>,
      stageFormValue: this.NormalizeStateValue(values.stageForm) as Record<string, unknown>,
      stageCalcFormValue: this.NormalizeStateValue(values.stageCalcForm) as Record<string, unknown>,
      gasCompFormValue: this.NormalizeStateValue(values.gasCompForm) as Record<string, unknown>,
    };
  }

  BuildFormChangeState(current: DleFormSnapshots, original: DleFormSnapshots, notes: string): DleChangeState {
    const isStep1Changed = !this.AreStateValuesEqual(current.initialFormValue, original.initialFormValue);
    const isStep2Changed =
      !this.AreStateValuesEqual(current.stageFormValue, original.stageFormValue) ||
      !this.AreStateValuesEqual(current.stageCalcFormValue, original.stageCalcFormValue);
    const isStep3Changed = !this.AreStateValuesEqual(current.gasCompFormValue, original.gasCompFormValue);
    const isNotesChanged = notes?.trim() !== commonConstants.emptyString;

    return {
      isStep1Changed,
      isStep2Changed,
      isStep3Changed,
      isNotesChanged,
      isChanged: isStep1Changed || isStep2Changed || isStep3Changed || isNotesChanged,
    };
  }

  ManipulateGrid1Data(grid1Data: Grid1[]): DLEGrid1Display[] {
    return grid1Data.map((data: Grid1) => {
      if (data.stage.toLowerCase() === DLE_CONSTANTS.pSat) {
        return {
          ...data,
          vVolStg: '--',
          vVolAmbient: '--',
          tempAmbient: '--',
          pressure: '--',
          vVolStd: '--',
          gasMW: '',
          stageGORstb: '',
          stageGORbbl: '',
          cumGOR: '',
          cumGORSatP: '',
          massRemoved: '--',
          molInCell: '--',
          gasDevFactor: '--',
          gasFVF: '--',
          gasGrav: '--'
        };
      }

      if (data.stage.toLowerCase() === DLE_CONSTANTS.lastStage) {
        return {
          ...data,
          vVolStg: '--',
          totVol: '--',
          gasDevFactor: '--'
        };
      }

      return data;
    });
  }

  ManipulateGrid2Data(grid2Data: Grid2[]): DLEGrid2Display[] {
    return grid2Data.map((data: Grid2) => {
      const stageName = (data as DLEGrid2Display).stages ?? data.stage;
      if (stageName.toLowerCase() === DLE_CONSTANTS.lastStageLocal) {
        return {
          ...data,
          gasVol: '',
          gasVolT: '',
          gasVolP: '',
          molVol: '',
          tubeLiq: '',
        };
      }

      return data;
    });
  }

  ClearGrid4DataAfterC13(grid4Data: Grid4[]): Grid4[] {
    const indexOfC13 = grid4Data.findIndex((item: Grid4) => item.compound.toLowerCase() === DLE_CONSTANTS.c13);
    return grid4Data.map((item: Grid4, index) => {
      if (index >= indexOfC13) {
        return {
          ...item,
          stg1MolPercentage: null,
          stg1Moles: null,
          stg2MolPercentage: null,
          stg2Moles: null,
          stg3MolPercentage: null,
          stg3Moles: null,
          stg4MolPercentage: null,
          stg4Moles: null,
          stg5MolPercentage: null,
          stg5Moles: null,
          stg6MolPercentage: null,
          stg6Moles: null,
          stg7MolPercentage: null,
          stg7Moles: null,
          stg8MolPercentage: null,
          stg8Moles: null,
          stg9MolPercentage: null,
          stg9Moles: null,
          stg10MolPercentage: null,
          stg10Moles: null,
          stg11MolPercentage: null,
          stg11Moles: null,
          stg12MolPercentage: null,
          stg12Moles: null,
          stg13MolPercentage: null,
          stg13Moles: null,
          stg14MolPercentage: null,
          stg14Moles: null,
          stg15MolPercentage: null,
          stg15Moles: null,
          stg16MolPercentage: null,
          stg16Moles: null,
          stg17MolPercentage: null,
          stg17Moles: null,
          stg18MolPercentage: null,
          stg18Moles: null,
          stg19MolPercentage: null,
          stg19Moles: null,
          stg20MolPercentage: null,
          stg20Moles: null,
          lastStageGasMolPercentage: null,
          lastStageGasMoles: null
        };
      }
      return item;
    }) as Grid4[];
  }

  GetStagePresenceFlags(grid1Data: { stage: string }[]): boolean[] {
    const stageCount = grid1Data.findIndex((row) => row.stage === PVT_CONSTANTS.LastStage) - 1;
    return this.GetVaporStageFlags(stageCount, 20);
  }

  MarkImageLogDataChanged() {
    this.imageLogDataChangedSource.next(true);
  }

  MarkEmptyTubeDataChanged() {
    this.emptyTubeDataChangedSource.next(true);
  }

   ResetImageLogDataChanged() {
    this.imageLogDataChangedSource.next(false);
  }

  ResetEmptyTubeDataChanged() {
    this.emptyTubeDataChangedSource.next(false);
  }

  BuildDefaultStoToReservoirRow(pressureComponent: string): DLESTOtoReservoirDisplay {
    return {
      pressureComponent,
      stoLiquidMoles: 0,
      stage21LiquidMoles: 0,
      stage20LiquidMoles: 0,
      stage19LiquidMoles: 0,
      stage18LiquidMoles: 0,
      stage17LiquidMoles: 0,
      stage16LiquidMoles: 0,
      stage15LiquidMoles: 0,
      stage14LiquidMoles: 0,
      stage13LiquidMoles: 0,
      stage12LiquidMoles: 0,
      stage11LiquidMoles: 0,
      stage10LiquidMoles: 0,
      stage9LiquidMoles: 0,
      stage8LiquidMoles: 0,
      stage7LiquidMoles: 0,
      stage6LiquidMoles: 0,
      stage5LiquidMoles: 0,
      stage4LiquidMoles: 0,
      stage3LiquidMoles: 0,
      stage2LiquidMoles: 0,
      stage1LiquidMoles: 0,
      reservoirFluidCalcMoleP: 0,
      reservoirFluidMeasuredMoleP: 0,
      absoluteMoleDiff: 0
    };
  }

  CreateEmptyVaporPressure(): DLEVaporStageCompositions {
    return {
      pressure: '',
      stage1: 0,
      stage2: 0,
      stage3: 0,
    };
  }

  BuildStagePresenceFlags(diffLibData: DLEDiffLiberation[]): boolean[] {
    const flags = Array.from({ length: 21 }, () => false);
    const stageCount = diffLibData.findIndex((item) => item.stage === PVT_CONSTANTS.STO) - 1;
    for (let stage = 1; stage <= stageCount && stage <= 21; stage++) {
      flags[stage - 1] = true;
    }
    return flags;
  }

  ManipulateDiffLibTestData(diffLibData: DLEDiffLiberation[]): DLEDiffLiberationDisplay[] {
    const stageCount = diffLibData.findIndex((item) => item.stage === PVT_CONSTANTS.STO) - 1;
    return diffLibData.map((data): DLEDiffLiberationDisplay => {
      if (data.stage.toLowerCase() === PVT_CONSTANTS.PSAT) {
        return {
          ...data,
          stageGORScfStb: '--',
          cumulativeGORScfStb: '--',
          cumulativeGORSatPScfBbl: '--',
          gasDeviationFactor: '--',
          gasFVF: '--',
          gasGravity: '--',
        };
      }

      if (data.stage === stageCount.toString()) {
        return {
          ...data,
          gasFVF: '--',
        };
      }

      if (data.stage.toLowerCase() === PVT_CONSTANTS.STO) {
        return {
          ...data,
          gasDeviationFactor: '--',
          gasFVF: '--',
          gasGravity: '--',
        };
      }

      return data;
    });
  }

  GetPreviousTabIndex(selectedIndex: number, tabCount: number): number {
    if (selectedIndex <= 0) {
      return 0;
    }
    return (selectedIndex - 1) % tabCount;
  }

  GetNextTabIndex(selectedIndex: number, tabCount: number): number {
    return (selectedIndex + 1) % tabCount;
  }

  CreateImageLogEmptyRow(): DLEImageLogFormRow {
    return {
      imageLogId: NUMBERS_CONSTANTS.zero,
      image: null,
      stage: null,
      pressure: null,
      temp: null,
      status: null,
      finalTubeWt: null,
      condensateWt: null,
      field: null,
      imageName: null,
      cellPressure: null,
      cellVolume: null,
      correctedCellVolume: null,
      cellTemperature: null,
      liquidHeight: null,
      liquidWidth: null,
      liquidArea: null,
      liquidVolume: null,
      gasVolume: null,
      correctedLiquidVolume: null,
      correctedGasVolume: null,
    };
  }

  ToImageLogFormRows(rows: DLEImageLog[]): DLEImageLogFormRow[] {
    return rows.map((row) => ({
      imageLogId: row.imageLogId,
      image: row.image,
      stage: row.stage,
      pressure: row.pressure,
      temp: row.temp,
      status: row.status,
      finalTubeWt: row.finalTubeWt,
      condensateWt: row.condensateWt,
      field: row.field,
      imageName: row.imageName,
      cellPressure: row.cellPressure,
      cellVolume: row.cellVolume,
      correctedCellVolume: row.correctedCellVolume,
      cellTemperature: row.cellTemperature,
      liquidHeight: row.liquidHeight,
      liquidWidth: row.liquidWidth,
      liquidArea: row.liquidArea,
      liquidVolume: row.liquidVolume,
      gasVolume: row.gasVolume,
      correctedLiquidVolume: row.correctedLiquidVolume,
      correctedGasVolume: row.correctedGasVolume,
    }));
  }

  NormalizeImageLogRows(rows: DLEImageLogFormRow[]): DLEImageLog[] {
    return rows.map((row) => ({
      imageLogId: row.imageLogId,
      image: this.ToNullableImageLogNumber(row.image),
      stage: this.ToNullableImageLogString(row.stage),
      pressure: this.ToNullableImageLogNumber(row.pressure),
      temp: this.ToNullableImageLogNumber(row.temp),
      status: this.ToNullableImageLogString(row.status),
      finalTubeWt: this.ToNullableImageLogNumber(row.finalTubeWt),
      condensateWt: this.ToNullableImageLogNumber(row.condensateWt),
      field: this.ToNullableImageLogNumber(row.field),
      imageName: this.ToNullableImageLogString(row.imageName),
      cellPressure: this.ToNullableImageLogNumber(row.cellPressure),
      cellVolume: this.ToNullableImageLogNumber(row.cellVolume),
      correctedCellVolume: this.ToNullableImageLogNumber(row.correctedCellVolume),
      cellTemperature: this.ToNullableImageLogNumber(row.cellTemperature),
      liquidHeight: this.ToNullableImageLogNumber(row.liquidHeight),
      liquidWidth: this.ToNullableImageLogNumber(row.liquidWidth),
      liquidArea: this.ToNullableImageLogNumber(row.liquidArea),
      liquidVolume: this.ToNullableImageLogNumber(row.liquidVolume),
      gasVolume: this.ToNullableImageLogNumber(row.gasVolume),
      correctedLiquidVolume: this.ToNullableImageLogNumber(row.correctedLiquidVolume),
      correctedGasVolume: this.ToNullableImageLogNumber(row.correctedGasVolume),
    }));
  }

  GetCleanImageLogData(rows: DLEImageLogFormRow[]): DLEImageLogFormRow[] {
    return rows.filter((row) => !this.IsImageLogRowEmpty(row));
  }

  IsImageLogRowEmpty(row: DLEImageLogFormRow): boolean {
    return Object.values(row).every((value) => !value);
  }

  SetImageLogValue(row: DLEImageLogFormRow, column: DLEImageLogColumn, value: string | null): void {
    row[column] = this.ParseImageLogValue(column, value);
  }

  IsImageLogColumn(column: string, headers: DLEImageLogColumn[]): column is DLEImageLogColumn {
    return (headers as readonly string[]).includes(column);
  }

  ApplyImageLogSectionPaste(
    rows: DLEImageLogFormRow[],
    pastedText: string,
    section: 'imageDetails' | 'imageVolDetails'
  ): DLEImageLogFormRow[] {
    const parsedRows = pastedText.split('\n').filter((row) => row.trim() !== MESSAGES_CONSTANTS.dleImageLogEmptyString);
    const updatedRows = [...rows];

    parsedRows.forEach((row, index) => {
      const columns = row.split('\t');
      if (!updatedRows[index]) {
        updatedRows[index] = this.CreateImageLogEmptyRow();
      }

      const targetRow = updatedRows[index];
      if (section === 'imageDetails') {
        targetRow.image = columns[NUMBERS_CONSTANTS.zero] ? Number(columns[NUMBERS_CONSTANTS.zero]) : NUMBERS_CONSTANTS.zero;
        targetRow.stage = columns[NUMBERS_CONSTANTS.one] ? columns[NUMBERS_CONSTANTS.one] : MESSAGES_CONSTANTS.dleImageLogEmptyString;
        targetRow.pressure = columns[NUMBERS_CONSTANTS.two] ? Number(columns[NUMBERS_CONSTANTS.two]) : NUMBERS_CONSTANTS.zero;
        targetRow.temp = columns[NUMBERS_CONSTANTS.three] ? Number(columns[NUMBERS_CONSTANTS.three]) : NUMBERS_CONSTANTS.zero;
        targetRow.status = columns[NUMBERS_CONSTANTS.four] ? columns[NUMBERS_CONSTANTS.four] : MESSAGES_CONSTANTS.dleImageLogEmptyString;
        targetRow.finalTubeWt = columns[NUMBERS_CONSTANTS.five] ? Number(columns[NUMBERS_CONSTANTS.five]) : NUMBERS_CONSTANTS.zero;
        targetRow.condensateWt = columns[NUMBERS_CONSTANTS.six] ? Number(columns[NUMBERS_CONSTANTS.six]) : NUMBERS_CONSTANTS.zero;
      } else {
        targetRow.field = columns[NUMBERS_CONSTANTS.zero] ? Number(columns[NUMBERS_CONSTANTS.zero]) : NUMBERS_CONSTANTS.zero;
        targetRow.imageName = columns[NUMBERS_CONSTANTS.one] ? columns[NUMBERS_CONSTANTS.one] : MESSAGES_CONSTANTS.dleImageLogEmptyString;
        targetRow.cellPressure = columns[NUMBERS_CONSTANTS.two] ? Number(columns[NUMBERS_CONSTANTS.two]) : NUMBERS_CONSTANTS.zero;
        targetRow.cellVolume = columns[NUMBERS_CONSTANTS.three] ? Number(columns[NUMBERS_CONSTANTS.three]) : NUMBERS_CONSTANTS.zero;
        targetRow.correctedCellVolume = columns[NUMBERS_CONSTANTS.four] ? Number(columns[NUMBERS_CONSTANTS.four]) : NUMBERS_CONSTANTS.zero;
        targetRow.cellTemperature = columns[NUMBERS_CONSTANTS.five] ? Number(columns[NUMBERS_CONSTANTS.five]) : NUMBERS_CONSTANTS.zero;
        targetRow.liquidHeight = columns[NUMBERS_CONSTANTS.six] ? Number(columns[NUMBERS_CONSTANTS.six]) : NUMBERS_CONSTANTS.zero;
        targetRow.liquidWidth = columns[NUMBERS_CONSTANTS.seven] ? Number(columns[NUMBERS_CONSTANTS.seven]) : NUMBERS_CONSTANTS.zero;
        targetRow.liquidArea = columns[NUMBERS_CONSTANTS.eight] ? Number(columns[NUMBERS_CONSTANTS.eight]) : NUMBERS_CONSTANTS.zero;
        targetRow.liquidVolume = columns[NUMBERS_CONSTANTS.nine] ? Number(columns[NUMBERS_CONSTANTS.nine]) : NUMBERS_CONSTANTS.zero;
        targetRow.gasVolume = columns[NUMBERS_CONSTANTS.ten] ? Number(columns[NUMBERS_CONSTANTS.ten]) : NUMBERS_CONSTANTS.zero;
        targetRow.correctedLiquidVolume = columns[NUMBERS_CONSTANTS.eleven] ? Number(columns[NUMBERS_CONSTANTS.eleven]) : NUMBERS_CONSTANTS.zero;
        targetRow.correctedGasVolume = columns[NUMBERS_CONSTANTS.twelve] ? Number(columns[NUMBERS_CONSTANTS.twelve]) : NUMBERS_CONSTANTS.zero;
      }
    });

    return updatedRows;
  }

  ApplyImageLogCellPaste(
    rows: DLEImageLogFormRow[],
    pastedData: string,
    rowIndex: number,
    column: DLEImageLogColumn,
    pageIndex: number,
    pageSize: number,
    displayColumns: string[],
    headers: DLEImageLogColumn[]
  ): DLEImageLogFormRow[] {
    const normalized = pastedData.replace(/\r\n/g, '\n').replace(/\r/g, '\n');
    const splitRows = normalized.split('\n');
    if (splitRows.length && splitRows[splitRows.length - NUMBERS_CONSTANTS.one] === MESSAGES_CONSTANTS.dleImageLogEmptyString) {
      splitRows.pop();
    }

    const parsedData = splitRows.map((r) => r.split('\t'));
    const updatedRows = [...rows];
    const globalRowIndex = (pageIndex * pageSize) + rowIndex;
    const requiredRows = globalRowIndex + parsedData.length;

    while (updatedRows.length < requiredRows) {
      updatedRows.push(this.CreateImageLogEmptyRow());
    }

    parsedData.forEach((cols, parsedRowIndex) => {
      const targetRow = globalRowIndex + parsedRowIndex;
      const startColIndex = displayColumns.indexOf(column);

      cols.forEach((value, colOffset) => {
        const colName = displayColumns[startColIndex + colOffset];
        if (colName && this.IsImageLogColumn(colName, headers)) {
          this.SetImageLogValue(updatedRows[targetRow], colName, value?.trim() || null);
        }
      });
    });

    return updatedRows;
  }

  ValidateImageLog(data: DLEImageLogFormRow[]): DleImageLogValidationResult {
    const issues: DleImageLogValidationIssue[] = [];
    const validator = /^\d*\.?\d*$/;

    const numericValidations: { column: DLEImageLogColumn; messagePrefix: string; fieldPrefix: string }[] = [
      { column: DLE_CONSTANTS.dleImageLogImage, messagePrefix: MESSAGES_CONSTANTS.dleImageLogEnterValidValueForImageForRowNumber, fieldPrefix: DLE_CONSTANTS.dleImageLogImage2 },
      { column: DLE_CONSTANTS.dleImageLogPressureSmall, messagePrefix: MESSAGES_CONSTANTS.dleImageLogEnterValidValueForPressureP1ForRowNumber, fieldPrefix: DLE_CONSTANTS.dleImageLogPressure2 },
      { column: DLE_CONSTANTS.dleImageLogTemp, messagePrefix: MESSAGES_CONSTANTS.dleImageLogEnterValidValueForTemperatureForRowNumber, fieldPrefix: DLE_CONSTANTS.dleImageLogTemp2 },
      { column: DLE_CONSTANTS.dleImageLogFinalTubeWt, messagePrefix: MESSAGES_CONSTANTS.dleImageLogEnterValidValueForFinalTubeWtForRowNumber, fieldPrefix: DLE_CONSTANTS.dleImageLogFinalTubeWt2 },
      { column: DLE_CONSTANTS.dleImageLogCondensateWt, messagePrefix: MESSAGES_CONSTANTS.dleImageLogEnterValidValueForCondensateWtForRowNumber, fieldPrefix: DLE_CONSTANTS.dleImageLogCondensateWt2 },
      { column: DLE_CONSTANTS.dleImageLogField, messagePrefix: MESSAGES_CONSTANTS.dleImageLogEnterValidValueForFieldForRowNumber, fieldPrefix: DLE_CONSTANTS.dleImageLogField2 },
      { column: DLE_CONSTANTS.dleImageLogCellPressure, messagePrefix: MESSAGES_CONSTANTS.dleImageLogEnterValidValueForCellPressureForRowNumber, fieldPrefix: DLE_CONSTANTS.dleImageLogCellPressure2 },
      { column: DLE_CONSTANTS.dleImageLogCellVolume, messagePrefix: MESSAGES_CONSTANTS.dleImageLogEnterValidValueForCellVolumeForRowNumber, fieldPrefix: DLE_CONSTANTS.dleImageLogCellVolume2 },
      { column: DLE_CONSTANTS.dleImageLogCorrectedCellVolume, messagePrefix: MESSAGES_CONSTANTS.dleImageLogEnterValidValueForCorectedCellVolumeForRowNumber, fieldPrefix: DLE_CONSTANTS.dleImageLogCorrectedCellVolume2 },
      { column: DLE_CONSTANTS.dleImageLogCellTemperature, messagePrefix: MESSAGES_CONSTANTS.dleImageLogEnterValidValueForCellTemperatureForRowNumber, fieldPrefix: DLE_CONSTANTS.dleImageLogCellTemperature2 },
      { column: DLE_CONSTANTS.dleImageLogLiquidHeight, messagePrefix: MESSAGES_CONSTANTS.dleImageLogEnterValidValueForLiquidHeightForRowNumber, fieldPrefix: DLE_CONSTANTS.dleImageLogLiquidHeight2 },
      { column: DLE_CONSTANTS.dleImageLogLiquidWidth, messagePrefix: MESSAGES_CONSTANTS.dleImageLogEnterValidValueForLiquidWidthForRowNumber, fieldPrefix: DLE_CONSTANTS.dleImageLogLiquidWidth2 },
      { column: DLE_CONSTANTS.dleImageLogLiquidArea, messagePrefix: MESSAGES_CONSTANTS.dleImageLogEnterValidValueForLiquidAreaForRowNumber, fieldPrefix: DLE_CONSTANTS.dleImageLogLiquidArea2 },
      { column: DLE_CONSTANTS.dleImageLogLiquidVolume, messagePrefix: MESSAGES_CONSTANTS.dleImageLogEnterValidValueForLiquidVolumeForRowNumber, fieldPrefix: DLE_CONSTANTS.dleImageLogLiquidVolume2 },
      { column: DLE_CONSTANTS.dleImageLogCorrectedLiquidVolume, messagePrefix: MESSAGES_CONSTANTS.dleImageLogEnterValidValueForCorrectedLiquidVolumeForRowNumber, fieldPrefix: DLE_CONSTANTS.dleImageLogCorrectedLiquidVolume2 },
      { column: DLE_CONSTANTS.dleImageLogCorrectedGasVolume, messagePrefix: MESSAGES_CONSTANTS.dleImageLogEnterValidValueForCorrectedGasVolumeForRowNumber, fieldPrefix: DLE_CONSTANTS.dleImageLogCorrectedGasVolume2 },
    ];

    data.forEach((row, index) => {
      numericValidations.forEach((item) => {
        const value = row[item.column];
        if (!this.IsBlankImageLogCellValue(value) && !validator.test(String(value))) {
          issues.push({
            fieldId: item.fieldPrefix + index,
            message: item.messagePrefix + (index + NUMBERS_CONSTANTS.one),
          });
        }
      });

      if (!this.IsBlankImageLogCellValue(row.imageName)) {
        const imageReg = new RegExp(/(^[a-zA-Z0-9.]{1,10}$)/);
        const allImages: string[] = [
          DLE_CONSTANTS.dleImageLogPng,
          DLE_CONSTANTS.dleImageLogJpg,
          DLE_CONSTANTS.dleImageLogJpeg,
          DLE_CONSTANTS.dleImageLogGif,
          DLE_CONSTANTS.dleImageLogBmp,
        ];
        const imageName = String(row.imageName);
        const fileType = imageName.slice(
          imageName.lastIndexOf(DLE_CONSTANTS.dleImageLogText) + NUMBERS_CONSTANTS.one
        );
        if (
          !imageReg.test(imageName.replace(DLE_CONSTANTS.dleImageLogText + fileType, MESSAGES_CONSTANTS.dleImageLogEmptyString)) ||
          !allImages.includes(fileType.toLowerCase()) ||
          imageName.indexOf(DLE_CONSTANTS.dleImageLogText) !== imageName.lastIndexOf(DLE_CONSTANTS.dleImageLogText)
        ) {
          issues.push({
            fieldId: DLE_CONSTANTS.dleImageLogImageName2 + index,
            message: MESSAGES_CONSTANTS.dleImageLogEnterValidFilenameFormatWithExtensionsJpgJpegPngGifBmp,
          });
        }
      }
    });

    return {
      status: issues.length === NUMBERS_CONSTANTS.zero,
      issues,
    };
  }

  private ConvertDashOrBlankToNull<T>(value: T): T | null {
    return value === '--' || value === '' ? null : value;
  }

  private ParseImageLogValue(column: DLEImageLogColumn, value: string | null): DLEImageLogCellValue {
    if (value === null || value === MESSAGES_CONSTANTS.dleImageLogEmptyString) {
      return null;
    }

    if (column === DLE_CONSTANTS.dleImageLogStageSmall || column === DLE_CONSTANTS.dleImageLogStatus || column === DLE_CONSTANTS.dleImageLogImageName) {
      return value;
    }

    const numericValue = Number(value);
    return Number.isNaN(numericValue) ? null : numericValue;
  }

  private IsBlankImageLogCellValue(value: DLEImageLogCellValue): boolean {
    return value === null || value === MESSAGES_CONSTANTS.dleImageLogEmptyString;
  }

  private ToNullableImageLogNumber(value: DLEImageLogCellValue | undefined): number | null {
    if (value === null || value === undefined || value === MESSAGES_CONSTANTS.dleImageLogEmptyString) {
      return null;
    }

    const numericValue = typeof value === DLE_CONSTANTS.dleImageLogNumber ? value : Number(value);
    return Number.isNaN(numericValue) ? null : Number(numericValue);
  }

  private ToNullableImageLogString(value: DLEImageLogCellValue | undefined): string | null {
    if (value === null || value === undefined || value === MESSAGES_CONSTANTS.dleImageLogEmptyString) {
      return null;
    }

    return String(value);
  }

  private AreStateValuesEqual(current: unknown, original: unknown): boolean {
    return JSON.stringify(this.NormalizeStateValue(current)) === JSON.stringify(this.NormalizeStateValue(original));
  }

  private toNumber(value: string | number | null): number | null {
    if (value === null || value === undefined) return null;
    const num = Number(value);
    return isNaN(num) ? null : num;
  }

  private ToNumberOrDefault(val: number | string | null | undefined, defaultValue = 0): number {
    if (val === null || val === '' || val === undefined) {
      return defaultValue;
    }
    const numValue = Number(val);
    return Number.isNaN(numValue) ? defaultValue : numValue;
  }

  private HasInputValue(value: string | number | null | undefined): boolean {
    return value !== undefined && value !== null && value !== '';
  }

  /**
   * Creates an empty stage input FormGroup with default structure
   */
  CreateStageInputFormGroup(): FormGroup {
    return new FormGroup({
      stageInputId: new FormControl(NUMBERS_CONSTANTS.zero),
      stage: new FormControl(commonConstants.emptyString),
      temperature: new FormControl(commonConstants.emptyString),
      pressure: new FormControl(commonConstants.emptyString),
      stageVVol: new FormControl(commonConstants.emptyString),
      ambientVVol: new FormControl(commonConstants.emptyString),
      ambientTemp: new FormControl(commonConstants.emptyString),
      ambientPressure: new FormControl(commonConstants.emptyString),
      stageLVol: new FormControl(commonConstants.emptyString),
      tubeLiqVol: new FormControl(commonConstants.emptyString),
      tubeLiqMass: new FormControl(commonConstants.emptyString),
      vaporEmthermMolDensity: new FormControl(commonConstants.emptyString),
      vaporEmthermGasDev: new FormControl(commonConstants.emptyString),
    });
  }

  /**
   * Creates stage input FormGroup with provided values
   */
  AddStageInput(
    stageInputId: number,
    stageValue: string,
    temperature: string,
    pressure: string,
    stageVVol: string,
    ambientVVol: string,
    ambientTemp: string,
    ambientPressure: string,
    stageLVol: string,
    tubeLiqVol: string,
    tubeLiqMass: string,
    vaporEmthermMolDensity: string,
    vaporEmthermGasDev: string
  ): FormGroup {
    return new FormGroup({
      stageInputId: new FormControl(stageInputId),
      stage: new FormControl(stageValue),
      temperature: new FormControl(temperature),
      pressure: new FormControl(pressure),
      stageVVol: new FormControl(stageVVol),
      ambientVVol: new FormControl(ambientVVol),
      ambientTemp: new FormControl(ambientTemp),
      ambientPressure: new FormControl(ambientPressure),
      stageLVol: new FormControl(stageLVol),
      tubeLiqVol: new FormControl(tubeLiqVol),
      tubeLiqMass: new FormControl(tubeLiqMass),
      vaporEmthermMolDensity: new FormControl(vaporEmthermMolDensity),
      vaporEmthermGasDev: new FormControl(vaporEmthermGasDev),
    });
  }

  /**
   * Creates an empty stage calc FormGroup
   */
  CreateStageCalcInputFormGroup(): FormGroup {
    return new FormGroup({
      stageCalcInputId: new FormControl(NUMBERS_CONSTANTS.zero),
      stage: new FormControl(commonConstants.emptyString),
      ambientVolume: new FormControl(null, { updateOn: 'blur' }),
      ambientPressure: new FormControl(null, { updateOn: 'blur' }),
      ambientTemperature: new FormControl(null, { updateOn: 'blur' }),
      volumeAtSTP: new FormControl(null),
    });
  }

  /**
   * Creates stage calc FormGroup with provided values
   */
  AddStageCalcInput(
    stageCalcInputId: number,
    stage: string,
    ambientVolume: number | null,
    ambientPressure: number | null,
    ambientTemperature: number | null,
    volumeAtSTP: number | null
  ): FormGroup {
    return new FormGroup({
      stageCalcInputId: new FormControl(stageCalcInputId),
      stage: new FormControl(stage),
      ambientVolume: new FormControl(ambientVolume, { updateOn: 'blur' }),
      ambientPressure: new FormControl(ambientPressure, { updateOn: 'blur' }),
      ambientTemperature: new FormControl(ambientTemperature, { updateOn: 'blur' }),
      volumeAtSTP: new FormControl(volumeAtSTP),
    });
  }

  /**
   * Creates an empty gas component FormGroup
   */
  CreateGasCompInputFormGroup(): FormGroup {
    return this.AddGasCompInput(
      NUMBERS_CONSTANTS.zero,
      commonConstants.emptyString,
      null, null, null, null, null, null, null, null,
      null, null, null, null, null, null, null, null,
      null, null, null, null, null, null, null,
      null, null, null, null
    );
  }

  /**
   * Creates gas component FormGroup with provided values
   */
  AddGasCompInput(
    gasCompId: number,
    gasComponents: string,
    blowDownCondensateWtPercent: number | null,
    blowDownOilWtPercent: number | null,
    resFluidFromSSF: number | null,
    vaporStg1: number | null,
    vaporStg2: number | null,
    vaporStg3: number | null,
    vaporStg4: number | null,
    vaporStg5: number | null,
    vaporStg6: number | null,
    vaporStg7: number | null,
    vaporStg8: number | null,
    vaporStg9: number | null,
    vaporStg10: number | null,
    vaporStg11: number | null,
    vaporStg12: number | null,
    vaporStg13: number | null,
    vaporStg14: number | null,
    vaporStg15: number | null,
    vaporStg16: number | null,
    vaporStg17: number | null,
    vaporStg18: number | null,
    vaporStg19: number | null,
    vaporStg20: number | null,
    vaporBlowDownMolP: number | null,
    blowdownTotalMolP: number | null,
    blowDownCondensateMol: number | null,
    blowDownCondensateMolP: number | null,
    isReadonly = false
  ): FormGroup {
    return new FormGroup({
      gasCompId: new FormControl(gasCompId),
      gasComponents: new FormControl(gasComponents),
      blowDownCondensateWtPercent: new FormControl({ value: blowDownCondensateWtPercent, disabled: isReadonly }),
      blowDownOilWtPercent: new FormControl({ value: blowDownOilWtPercent, disabled: isReadonly }),
      resFluidFromSSF: new FormControl({ value: resFluidFromSSF, disabled: isReadonly }),
      vaporStg1: new FormControl({ value: vaporStg1, disabled: isReadonly }),
      vaporStg2: new FormControl({ value: vaporStg2, disabled: isReadonly }),
      vaporStg3: new FormControl({ value: vaporStg3, disabled: isReadonly }),
      vaporStg4: new FormControl({ value: vaporStg4, disabled: isReadonly }),
      vaporStg5: new FormControl({ value: vaporStg5, disabled: isReadonly }),
      vaporStg6: new FormControl({ value: vaporStg6, disabled: isReadonly }),
      vaporStg7: new FormControl({ value: vaporStg7, disabled: isReadonly }),
      vaporStg8: new FormControl({ value: vaporStg8, disabled: isReadonly }),
      vaporStg9: new FormControl({ value: vaporStg9, disabled: isReadonly }),
      vaporStg10: new FormControl({ value: vaporStg10, disabled: isReadonly }),
      vaporStg11: new FormControl({ value: vaporStg11, disabled: isReadonly }),
      vaporStg12: new FormControl({ value: vaporStg12, disabled: isReadonly }),
      vaporStg13: new FormControl({ value: vaporStg13, disabled: isReadonly }),
      vaporStg14: new FormControl({ value: vaporStg14, disabled: isReadonly }),
      vaporStg15: new FormControl({ value: vaporStg15, disabled: isReadonly }),
      vaporStg16: new FormControl({ value: vaporStg16, disabled: isReadonly }),
      vaporStg17: new FormControl({ value: vaporStg17, disabled: isReadonly }),
      vaporStg18: new FormControl({ value: vaporStg18, disabled: isReadonly }),
      vaporStg19: new FormControl({ value: vaporStg19, disabled: isReadonly }),
      vaporStg20: new FormControl({ value: vaporStg20, disabled: isReadonly }),
      vaporBlowDownMolP: new FormControl({ value: vaporBlowDownMolP, disabled: isReadonly }),
      blowdownTotalMolP: new FormControl({ value: blowdownTotalMolP, disabled: isReadonly }),
      blowDownCondensateMol: new FormControl({ value: blowDownCondensateMol, disabled: isReadonly }),
      blowDownCondensateMolP: new FormControl({ value: blowDownCondensateMolP, disabled: isReadonly }),
    });
  }

  /**
   * Disables stage input controls based on stage type
   */
  DisableStageInputsByStageType(stageControls: AbstractControl[]): void {
    const psatCols = DLE_CONSTANTS.psatCols || [];
    stageControls.forEach((control) => {
      const group = control as FormGroup;
      const stage = group.get(CVD_CONSTANTS.stage)?.value;
      group.get(CVD_CONSTANTS.temperature2)?.disable();
      if (stage === PVT_CONSTANTS.BlowdownStage) {
        group.get(DLE_CONSTANTS.stageVVol)?.disable();
      }
      if (stage === PVT_CONSTANTS.PSAT || stage === PVT_CONSTANTS.STO) {
        psatCols.forEach(col => {
          group.get(col)?.disable();
        });
      }
    });
  }

  /**
   * Updates temperature values in stage forms based on reservoir temperature
   */
  UpdateStageFormTemperatureValues(stageControls: FormGroup[], reservoirTemperature: string | number | null): void {
    stageControls.forEach((group) => {
      const stage = group.get(CVD_CONSTANTS.stage)?.value;
      if (stage !== PVT_CONSTANTS.PSAT && stage !== PVT_CONSTANTS.STO) {
        group.get(CVD_CONSTANTS.temperature)?.setValue(reservoirTemperature);
      }
    });
  }
}
